import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useModel } from '../ModelProvider';
import OnboardingIncomplete from '../components/OnboardingIncomplete';
import ModelLoadError from '../components/ModelLoadError';
import ViewOnly, { viewOnlyInertProps } from '../components/ViewOnly';
import { getVendorsWithActuals, createVendor as apiCreateVendor, updateVendor as apiUpdateVendor, deleteVendor as apiDeleteVendor, bulkUpsertVendorActuals, bulkUpdateVendors, getPrepaidContracts, createPrepaidContract as apiCreatePrepaid, updatePrepaidContract as apiUpdatePrepaid, deletePrepaidContract as apiDeletePrepaid, getCompanySettings } from '../api';
import { filterActuals, filterForecast, getYear, formatMonthLabel } from '../engines/dateUtils';
import { buildISLineTaxonomy, ISLinePicker, defaultVendorIsLine, buildPayrollGreyedEntries } from '../components/ISLinePicker';
// c2: buildCycleAmort/buildForecastTail (and addMonths) were used ONLY by PrepaidModal,
// which now lives in components/PrepaidModal.jsx and imports them directly.
import { buildPrepaidLabelToLine } from '../engines/prepaidRouting';
// The ONE owner of a prepaid vendor's per-vendor forecast, with its End Month applied -- the same set the engine books.
import { prepaidForecastByVendor } from '../engines/prepaidTermination';
// What the Prepaid tab and the Vendor Modal SHOW about prepaid contracts -- it calls the same rule, never restates it.
import { prepaidDroppedContracts, prepaidVendorSummary } from '../utils/prepaidDisplay';
import { buildVendorForecastMd, ERR_NO_IS_LINE, assertVendorRouted } from '../utils/vendorForecast';
import { buildSeatDeptResolver } from '../utils/seatDepartments';
import { isAlreadyGone, deleteFailureText } from '../utils/alreadyGone';
import { PCT_REVENUE_TOTAL, PCT_REVENUE_TOTAL_LABEL, PCT_REVENUE_TOTAL_HELP, PCT_RATE_MESSAGES, pctRevenueBaseOptions } from '../utils/pctRevenueSource';   // W-073 (b); 2026-10-27 B1 / B2 / B5
import { parsePercentText, percentText } from '../utils/percentText';   // B5: the one percentage-typing rule
import GuardedDialog from '../components/GuardedDialog';   // Batch 1b (L3641): the Vendor Modal's unsaved-changes guard (it calls useUnsavedChangesWarning)
/* W-028: vendor names sort NATURALLY ("Contractor 2" before "Contractor 10") through the one comparator the
   employee lists use. It is also case-insensitive (sensitivity 'base'); measured inert on every tenant's names. */
import { compareNamesNaturally } from '../engines/payrollKeys';
import ForecastWizard from './ForecastWizard';
// Build 3 c2: PrepaidModal lifted out of this file so the Step-6 wizard embeds the SAME
// component. PAY_FREQ + ML moved with it (one declaration, two importers — not a copy).
import PrepaidModal, { PAY_FREQ } from '../components/PrepaidModal';
// Prepaid renewals part 2 (2026-10-29): the ONE planner + the shared Renews control (the Step-6 wizard uses the same).
import { prepaidRenewsView, planPrepaidRenewsAnswer, planPrepaidEdit, planPrepaidFlaggedKeep, planPrepaidFlaggedDelete, executePrepaidPlan, prepaidCollisionText, prepaidDuplicateText, prepaidRowLabel, prepaidDisplayOrder } from '../utils/prepaidRenewals';
import { PrepaidRenewsCell, PrepaidRenewalChip, PrepaidRenewsFlagged } from '../components/PrepaidRenewsCell';

/* ═══════════════════════════════════════════════════════════════════════════════
   IS CATEGORY HIERARCHY — derived from rawData.glMappings at render time via the
   SHARED buildISLineTaxonomy (client/src/components/ISLinePicker.jsx). The prior
   module-scope hardcoded IS_CATS / SECTIONS / DISABLED_VENDOR_CATS arrays were
   Semitek-shaped and silently misclassified non-Semitek tenants (e.g. AE Model's
   34 per-line is_line values). Path C Session 5b grouping (ii) sub-commit 1
   converted all 8 in-page consumers (modal dropdown, computeVendors bucketing,
   IS Summary table, Prepaid catLabel, All Vendors filter/sort/render, Dashboard
   By Section panel) to a single data-driven derivation. Session "Scenarios clear"
   extracted the builder + WAGES_IS_LINES/SECTION_COLOR/SECTION_ORDER + the cascading
   picker into the shared module so ScenariosPage reuses the SAME picker (no lookalike).
   ═══════════════════════════════════════════════════════════════════════════════ */


const VENDOR_TYPES = [
  { id: "constant", label: "Fixed Monthly", desc: "Same $ every month from start date forward" },
  { id: "prepaid", label: "Prepaid", desc: "Paid upfront — cycles & amortization live on the Prepaid tab" },
  { id: "pct_revenue", label: "% of Revenue", desc: "Percentage of monthly revenue" },
  { id: "one_off", label: "One-Off / Manual", desc: "Budget specific amounts for specific months" },
  { id: "per_seat", label: "Per Seat", desc: "Cost per person × headcount each month" },
];

const SEAT_BASIS_OPTIONS = [
  { id: "fte", label: "FTE Only" },
  { id: "contractors", label: "Contractors Only" },
  { id: "both", label: "FTE + Contractors" },
];

// ★ Item 7: the hardcoded Semitek SEAT_DEPARTMENTS list is GONE from this file.
// The option list now comes from the tenant's own department_mappings via the
// shared resolver (buildSeatDeptResolver), threaded to the modal as a prop; the
// Semitek six survive only as that resolver's LEGACY branch for cid=1/cid=2.

const r2 = n => Math.round(n * 100) / 100;
const fmt = n => !n||n===0?"—":new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:2,maximumFractionDigits:2}).format(r2(n));
const fmtK = n => {if(!n||n===0) return "—";const v=r2(n);if(Math.abs(v)>=1e6) return `$${(v/1e6).toFixed(2)}M`;if(Math.abs(v)>=1e4) return `$${(v/1e3).toFixed(1)}K`;return fmt(v);};
const fmtPct = n => n ? `${percentText(n)}%` : "—";   // N-e (2026-11-03): the one percentage owner, as typed (97.25%, not 97.3%)
const uid = () => Math.random().toString(36).slice(2,9);

// Session 5f-pre sc-4a: the local single-cycle straight-line amortizer
// (buildStraightLineAmort) was converged onto the engine helper buildCycleAmort
// (client/src/engines/prepaidAmort.js) — byte-identical (sc-1 Phase 2, 6 cases).
// One slicer for the single-contract PrepaidModal save AND sc-4's recurring-series
// generation. Still SEPARATE from the Vendor Modal's multi-cycle/renewal generator
// (Option-A). Last month takes the remainder so the BS prepaid rolls to exactly $0
// at schedule end; indefinite (no/<=0 length) → {} (parks the full amount on the BS).
// mIdx/mFromIdx/mOffset/mAdd migrated to dateUtils.addMonths and dateUtils.monthOffset (Sub-phase 1.5 pattern; no local definitions remaining).
// MONTHS_36/ACTUALS/FORECAST/isForecast/DC/DC0 migrated to component-body memos + cutoff-aware closures (3.5a tenant-aware conversion).

/* ═══════════════════════════════════════════════════════════════════════════════
   COMPUTE ENGINE
   ═══════════════════════════════════════════════════════════════════════════════ */
function computeVendors(vendors, months, taxonomy, cutoff) {
  // Group by IS category — buckets sourced from taxonomy.ordered (data-driven)
  const byCat = {};
  taxonomy.ordered.forEach(e => { byCat[e.id] = { vendors: [], totals: {} }; months.forEach(m => { byCat[e.id].totals[m] = 0; }); });
  const grand = {}; months.forEach(m => { grand[m] = 0; });
  const bySection = {}; taxonomy.sections.forEach(s => { bySection[s.id] = {}; months.forEach(m => { bySection[s.id][m] = 0; }); });

  // Sub-commit 3.1: archive-aware historical analysis. Archived vendors enter
  // cat.vendors so they list in Expense Build's historical view, but only
  // contribute to per-month totals for historical months (m <= cutoff).
  // Forecast months (m > cutoff) get $0 from archived vendors — matches the
  // engine's exclusion at computeVendor.js:108/128 + runFullModel.js:374.
  // Cutoff semantics per VendorDashboard.js:1044: isForecast = m > cutoff.
  vendors.forEach(v => {
    const cat = byCat[v.isCat];
    if (!cat) return;
    const section = taxonomy.byKey.get(v.isCat)?.section;
    cat.vendors.push(v);
    months.forEach(m => {
      /* An archived NON-prepaid vendor books nothing in the forecast (computeVendor), so its months are skipped. An archived
       * PREPAID vendor is different: the model keeps booking its contracts (runFullModel section 12b reads no active flag), so
       * its forecast stays in the totals -- the screen agrees with the model. Its End Month is what terminates it. */
      if (!v.active && m > cutoff && v.type !== "prepaid") return;
      const amt = r2(v.monthlyData[m] || 0);
      cat.totals[m] = r2(cat.totals[m] + amt);
      grand[m] = r2(grand[m] + amt);
      if (section && bySection[section]) bySection[section][m] = r2(bySection[section][m] + amt);
    });
  });

  // Prepaid vendors: type === "prepaid" (sc-rework-5 — canonical; not freq-based)
  const prepaidVendors = vendors.filter(v => v.active && v.type === "prepaid");
  const monthlyVendors = vendors.filter(v => v.active && v.type !== "prepaid");

  return { byCat, grand, bySection, prepaidVendors, monthlyVendors };
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MODAL
   ═══════════════════════════════════════════════════════════════════════════════ */
function VendorModal({ canEdit = false, vendor, onSave, onClose, onDelete, actualsMonths, forecastMonths, firstForecast, lastForecast, forecastYearRangeLabel, isLineTaxonomy, defaultIsCat, payrollGreyed, pctRevenueSourceOptions, vendors, prepaidContracts, deleteError, saveError, seatDeptOptions, prepaidCutoff, prepaidLabelToLine }) {
  // ★ Item 7: the tenant's real departments (from department_mappings via the shared
  // resolver); the Semitek six only appear when the tenant has none (legacy branch).
  const SEAT_DEPARTMENTS = seatDeptOptions || [];
  const isNew = !vendor;
  const startFallback = firstForecast || "";
  const [f, setF] = useState(vendor || {
    id: uid(), name: "", isCat: defaultIsCat, type: "constant", freq: "monthly",
    amount: 0, pctRate: 0, monthlyData: {}, notes: "", active: true,
    startDate: startFallback, endDate: "until_canceled", renewals: [], amortStart: "",
    isContractor: false, contractorCount: 1, contractorMonthly: {},
    perSeatCost: 0, seatBasis: "fte", seatDepartments: [],
  });
  const initEntries = () => {
    if (!vendor || vendor.type !== "one_off") return [{ month: startFallback, amount: 0 }];
    const entries = forecastMonths.filter(m => (vendor.monthlyData[m]||0) > 0)
      .map(m => ({ month: m, amount: vendor.monthlyData[m] }));
    return entries.length > 0 ? entries : [{ month: startFallback, amount: 0 }];
  };
  const [oneOffEntries, setOneOffEntries] = useState(initEntries);
  const [renewals, setRenewals] = useState(vendor?.renewals || []);
  const [pctSrcBlocked, setPctSrcBlocked] = useState(false);   // W-073 (b): Save pressed with no revenue base
  // B5: the "% of Revenue" box's text as typed (a stored 0.73 shows "73"); refused text keeps f.pctRate as it was and is named inline
  // (the Forecast Wizard's cell seeds the same way: a stored pct_revenue vendor shows its rate -- a stored 0 shows "0" -- else blank)
  const [pctText, setPctText] = useState(() => (vendor && vendor.type === 'pct_revenue' && vendor.pctRate != null ? percentText(vendor.pctRate) : ''));
  const pctTextError = parsePercentText(pctText, PCT_RATE_MESSAGES).error;
  const [allDepts, setAllDepts] = useState(!vendor?.seatDepartments || vendor.seatDepartments.length === 0);

  // Sub-commit 5b-safety: capture initial state snapshot on first
  // render so we can detect dirty state for the beforeunload warning.
  // Includes f + renewals + oneOffEntries (the three editable state
  // surfaces — allDepts is UI-only and its effects flow through f).
  // useRef lazy-init handles uid()-generated new-vendor templates:
  // captures the actual generated id once, not regenerated per
  // comparison.
  const initialSnapshot = useRef(null);
  if (initialSnapshot.current === null) {
    initialSnapshot.current = JSON.stringify({ f, renewals, oneOffEntries });
  }
  const isDirty = useMemo(
    () => JSON.stringify({ f, renewals, oneOffEntries }) !== initialSnapshot.current,
    [f, renewals, oneOffEntries]
  );

  // Browser-close protection: warn operator if attempting to leave the page with
  // unsaved Vendor Modal edits. Critical for long data-entry sessions (6-10h
  // entering 229 AE Model vendors) where an accidental tab close loses everything.
  // ★ The effect itself now lives in ONE place (utils/useUnsavedChangesWarning) —
  // this was one of three byte-identical copies. The dirty DERIVATION above stays
  // here, because what counts as dirty is this surface's own question.
  /* Batch 1b (2026-11-03, L3641): the unsaved-changes guard is components/GuardedDialog.jsx (it calls useUnsavedChangesWarning -- the one
     beforeunload guard); this dialog keeps its own footer note (note={null}). */

  const iS = { width:"100%",padding:"10px 12px",background:"#F0F1F5",border:"1px solid rgba(0,0,0,0.12)",borderRadius:"8px",color:"#1A1C2E",fontSize:"14px",fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box" };
  const lS = { display:"block",fontSize:"11px",fontWeight:600,color:"#6B7094",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"6px" };

  const isPrepaid = f.type === "prepaid";   // sc-rework-5: canonical — type, not freq
  const freqMonths = PAY_FREQ.find(p => p.id === f.freq)?.months || 1;

  const startMk = f.startDate || startFallback;
  const endMk = f.endDate || "until_canceled";
  const isUntilCanceled = endMk === "until_canceled";
  const isMonthActive = m => m >= startMk && (isUntilCanceled || m <= endMk);

  const updateEntry = (idx, key, val) => {
    const u = [...oneOffEntries]; u[idx] = {...u[idx],[key]:val}; setOneOffEntries(u);
  };
  const addEntry = () => setOneOffEntries([...oneOffEntries, { month: startFallback, amount: 0 }]);
  const removeEntry = idx => setOneOffEntries(oneOffEntries.filter((_,i) => i !== idx));

  /* PREPAID (Brian 2026-09-25): a prepaid vendor's forecast lives in its CONTRACTS, not in this row's start -> end months
     (its start_date is the import's "next payment" snapshot, often after the window, which is how the line came to read
     "0 forecast months (Feb '24 -> Jun '23)"). So for a prepaid vendor the line summarises the contracts as the model books
     them -- utils/prepaidDisplay, run on the vendor set carrying THIS FORM's End Month, so it follows the End Month as it is
     changed, before saving. The Payment Month below reads the same summary. An ordinary vendor's line is unchanged. */
  const prepaidSummary = useMemo(() => {
    if (!isPrepaid) return null;
    const previewVendors = (vendors || []).map(v => v.id === f.id ? { ...v, endDate: f.endDate } : v);
    return prepaidVendorSummary(f.id, prepaidContracts, previewVendors, { cutoff: prepaidCutoff, lineOf: vv => vv.isCat, labelToLine: prepaidLabelToLine });
  }, [isPrepaid, vendors, f.id, f.endDate, prepaidContracts, prepaidCutoff, prepaidLabelToLine]);
  const prepaidContractLine = () => {
    const ps = prepaidSummary;
    if (!ps || ps.count === 0) return "no contracts yet \u2014 add them on the Prepaid tab";
    const parts = [`${ps.count} contract${ps.count === 1 ? "" : "s"}`];
    if (ps.lastMonth) parts.push(`the last ${ps.lastMonth > (prepaidCutoff || "") ? "runs" : "ran"} out ${formatMonthLabel(ps.lastMonth, "shortYear")}`);
    if (ps.cancelled) parts.push(`${ps.cancelled} cancelled by the End Month`);
    return parts.join(" \u00b7 ");
  };
  const prepaidPaymentText = () => {
    const ps = prepaidSummary;
    if (ps && ps.nextPayment) return formatMonthLabel(ps.nextPayment, "shortYear");
    if (ps && ps.cancelled) return "none (cancelled by End Month)";
    return "none scheduled";
  };
  const contractInfo = () => {
    if (f.type === "one_off") return null;
    if (isPrepaid) return prepaidContractLine();
    const activeMonths = forecastMonths.filter(isMonthActive).length;
    if (isUntilCanceled) return `${activeMonths} forecast months (ongoing)`;
    return `${activeMonths} forecast months (${formatMonthLabel(startMk, "shortYear")} \u2192 ${formatMonthLabel(endMk, "shortYear")})`;
  };

  // Sub-commit 4: within-is_line duplicate-name soft warning. Scope is
  // (f.isCat, f.name) \u2014 fires only on accidental in-category duplicates.
  // AE Model's Misc \u00d7 9 pattern (one Misc per category) stays quiet because
  // each Misc is in a distinct is_line. Self-exclusion via v.id !== f.id
  // works in both modes: edit (real ids match), create (uid()-generated
  // f.id never collides with existing real ids).
  /* W-047.2: compared NORMALISED -- an exact === missed "Acme  Inc" next to "acme inc" (a false NEGATIVE).
     Only the warning changes; the name is saved exactly as typed. */
  const normVendorName = s => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const duplicateVendors = normVendorName(f.name)
    ? (vendors || []).filter(v => v.id !== f.id && v.isCat === f.isCat && normVendorName(v.name) === normVendorName(f.name))
    : [];
  const duplicateCount = duplicateVendors.length;

  // Session 5d-1: advisory rename warning. Count prepaid contracts linked to
  // THIS vendor (by vendor_id when present, else by current vendor_name for the
  // display count only — the server cascade is authoritative). Shown when the
  // name field is dirty so the operator knows the rename will propagate.
  const nameDirty = !isNew && vendor && f.name !== vendor.name;
  const linkedPrepaidCount = (!isNew && vendor && typeof vendor.id === "number")
    ? (prepaidContracts || []).filter(c =>
        c.vendorId != null ? c.vendorId === vendor.id : c.vendor === vendor.name).length
    : 0;

  return (
        <GuardedDialog isDirty={isDirty} onClose={onClose} note={null} cardStyle={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.1)",borderRadius:"20px",padding:"32px",width:"640px",maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,0.15)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"24px"}}>
          <h3 style={{margin:0,fontSize:"20px",fontWeight:700,color:"#1A1C2E"}}>{isNew?"Add Vendor":"Edit Vendor"}{isDirty && <span style={{color:"#E67E22",fontWeight:700,marginLeft:"6px"}}>*</span>}</h3>
          {isPrepaid && <div title={"Contract amounts & schedule live on the Prepaid tab \u2014 this vendor is just flagged prepaid here."} style={{fontSize:"11px",padding:"4px 10px",borderRadius:"6px",background:"rgba(46,134,193,0.1)",color:"#2E86C1",fontWeight:700,cursor:"help"}}>{"\u2192"} Flows to Prepaid Tab</div>}
          {!isPrepaid && f.type==="constant" && <div style={{fontSize:"11px",padding:"4px 10px",borderRadius:"6px",background:"rgba(39,174,96,0.1)",color:"#27AE60",fontWeight:700}}>{"\u2192"} Direct to Exp Build</div>}
          {f.type==="one_off" && <div style={{fontSize:"11px",padding:"4px 10px",borderRadius:"6px",background:"rgba(212,172,13,0.1)",color:"#B7950B",fontWeight:700}}>Manual Budget Entry</div>}
        </div>
        <ViewOnly canEdit={canEdit}><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px"}}>
          <div style={{gridColumn:"1/-1"}}>
            <label style={lS}>Vendor Name</label>
            <input style={iS} value={f.name} onChange={e=>setF({...f,name:e.target.value})}/>
            {duplicateCount > 0 && (
              <div style={{fontSize:"11px",color:"#C0392B",marginTop:"4px"}}>
                This category already has {duplicateCount === 1 ? `a vendor named '${duplicateVendors[0].name}'.` : `${duplicateCount} vendors named like '${duplicateVendors[0].name}'.`}
              </div>
            )}
            {nameDirty && linkedPrepaidCount > 0 && (
              <div style={{fontSize:"11px",color:"#2E86C1",marginTop:"4px"}}>
                Renaming will also update {linkedPrepaidCount} linked prepaid contract{linkedPrepaidCount === 1 ? "" : "s"}.
              </div>
            )}
          </div>
          <div>
            <label style={lS}>IS Category</label>
            <ISLinePicker taxonomy={isLineTaxonomy} value={f.isCat} onChange={id => setF({...f, isCat: id})} style={iS} extraGreyed={payrollGreyed} />
          </div>
          <div><label style={lS}>Vendor Type</label>
            <select style={iS} value={f.type} onChange={e=>setF({...f,type:e.target.value})}>
              {VENDOR_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>{VENDOR_TYPES.find(t=>t.id===f.type)?.desc}</div>
          </div>
          <div><label style={lS}>Payment Frequency</label>
            <select style={iS} value={f.freq} onChange={e=>{
              // sc-rework-5: freq is decoupled from type — set ONLY freq. Prepaid-ness
              // is the TYPE (chosen in the Vendor Type dropdown). The old newType
              // coercion is gone; the endDate recompute was a no-op (newEnd===f.endDate).
              setF({...f, freq: e.target.value});
            }}>
              {PAY_FREQ.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>

          {f.type !== "one_off" && <>
            <div>
              <label style={lS}>{isPrepaid?"Payment Month":"Start Month"}</label>
              {/* PREPAID: read-only, from the contracts (prepaidSummary). A prepaid vendor's stored start_date can lie outside the
                  window, where a select would silently show its FIRST option and one click would write that month. The field no
                  longer offers a write: f.startDate passes through a save unchanged. */}
              {isPrepaid ? <div style={{...iS,background:"#F7F8FA",color:"#4A4E69"}}>{prepaidPaymentText()}</div> : <select style={iS} value={f.startDate||startFallback} onChange={e=>{
                setF({...f, startDate: e.target.value});
              }}>
                {forecastMonths.map(m=><option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
              </select>}
              {isPrepaid && <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>The next payment in this vendor's contracts (Prepaid tab) {"\u2014"} read-only</div>}
            </div>
            <div>
              <label style={lS}>End Month</label>
              <select style={iS} value={f.endDate||"until_canceled"} onChange={e=>setF({...f,endDate:e.target.value})}>
                <option value="until_canceled">Until Canceled</option>
                {forecastMonths.filter(m => isPrepaid || m >= (f.startDate||startFallback)).map(m=><option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
              </select>
              {isUntilCanceled && <div style={{fontSize:"10px",color:"#27AE60",marginTop:"4px"}}>Auto-renews {"\u2014"} continues until you cancel</div>}
              {!isUntilCanceled && <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>{isPrepaid ? "Cancellation month \u2014 renewals starting in or after this month are dropped. The period already paid runs to its end." : "Last month of charges"}</div>}
            </div>
            {contractInfo() && <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:"11px",color:"#6B7094",padding:"8px 12px",borderRadius:"8px",background:"rgba(0,0,0,0.02)",border:"1px solid rgba(0,0,0,0.04)"}}>
                <span style={{fontWeight:600}}>Contract: </span>{contractInfo()}
              </div>
            </div>}
          </>}

          {f.type==="constant" && <div>
            <label style={lS}>Monthly Amount</label>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:"12px",top:"50%",transform:"translateY(-50%)",color:"#8B8FA3",fontSize:"14px",pointerEvents:"none"}}>$</span>
              <input style={{...iS,paddingLeft:"24px",MozAppearance:"textfield"}} type="text" inputMode="decimal"
                value={f.amount ? f.amount.toLocaleString("en-US") : ""}
                onChange={e=>{const raw=e.target.value.replace(/[^0-9.]/g,"");setF({...f,amount:parseFloat(raw)||0});}}
                placeholder="0"/>
            </div>
          </div>}
          {/* sc-rework-3 (single-source): prepaid is a flag — no amount field here; the
              replacement note keeps the missing field legible. Amounts/amortization live
              on the Prepaid tab (PrepaidModal), shown in the grid via the rework-1 overlay. */}
          {isPrepaid && <div style={{gridColumn:"1/-1",fontSize:"11px",color:"#2E86C1",marginTop:"2px",fontStyle:"italic"}}>Amounts & amortization are entered on the Prepaid tab.</div>}

          {/* Renewal Pricing — CONSTANT vendors only (prepaid renewals are separate
              stored contracts on the Prepaid tab under single-source, sc-rework-3). */}
          {!isPrepaid && f.type==="constant" && f.amount > 0 && <div style={{gridColumn:"1/-1",borderTop:"1px solid rgba(0,0,0,0.06)",paddingTop:"16px",marginTop:"4px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"10px"}}>
              <div>
                <div style={{fontSize:"12px",fontWeight:700,color:"#6B7094"}}>PRICE CHANGES &amp; RENEWALS</div>
                <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"2px"}}>Schedule a known price change from any forecast month. Current {isPrepaid?"payment":"monthly"}: <span style={{fontFamily:"'Space Mono',monospace",fontWeight:600}}>{fmt(f.amount || (isPrepaid ? (Math.max(...forecastMonths.map(m=>f.monthlyData[m]||0)) * freqMonths) : 0))}</span></div>
              </div>
              <button onClick={()=>setRenewals([...renewals,{month:forecastMonths.find(m=>m>=(f.startDate||startFallback))||startFallback,amount:0}])} style={{padding:"5px 14px",borderRadius:"7px",border:"1px solid rgba(46,134,193,0.2)",background:"rgba(46,134,193,0.05)",color:"#2E86C1",cursor:"pointer",fontSize:"12px",fontWeight:600,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>+ Add price change / renewal</button>
            </div>
            {renewals.length===0 && <div style={{fontSize:"11px",color:"#B0B3C8",fontStyle:"italic",padding:"8px 0"}}>No price changes scheduled. Click "+ Add price change / renewal" to change the monthly price from a forecast month on (a mid-forecast price step, or a renewal at a different price).</div>}
            {renewals.length>0 && <div style={{display:"flex",flexDirection:"column",gap:"8px",maxHeight:"180px",overflowY:"auto",paddingRight:"4px"}}>
              {renewals.map((r, idx) => (
                <div key={idx} style={{display:"flex",gap:"10px",alignItems:"center",padding:"8px 12px",borderRadius:"8px",background:"rgba(46,134,193,0.03)",border:"1px solid rgba(46,134,193,0.08)"}}>
                  <div style={{flex:"0 0 auto",fontSize:"11px",fontWeight:600,color:"#2E86C1"}}>Effective</div>
                  <select style={{...iS,flex:"0 0 130px",padding:"7px 10px",fontSize:"12px"}} value={r.month} onChange={e=>{const u=[...renewals];u[idx]={...u[idx],month:e.target.value};setRenewals(u);}}>
                    {forecastMonths.map(m=><option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
                  </select>
                  <div style={{flex:"0 0 auto",fontSize:"11px",color:"#6B7094"}}>New {isPrepaid?"payment":"amount"}</div>
                  <div style={{position:"relative",flex:1}}>
                    <span style={{position:"absolute",left:"10px",top:"50%",transform:"translateY(-50%)",color:"#8B8FA3",fontSize:"13px",pointerEvents:"none"}}>$</span>
                    <input style={{...iS,paddingLeft:"22px",padding:"7px 10px 7px 22px",fontSize:"12px"}} type="text" inputMode="decimal"
                      value={r.amount ? r.amount.toLocaleString("en-US") : ""}
                      onChange={e=>{const raw=e.target.value.replace(/[^0-9.]/g,"");const u=[...renewals];u[idx]={...u[idx],amount:parseFloat(raw)||0};setRenewals(u);}}
                      placeholder="0"/>
                  </div>
                  {r.amount > 0 && isPrepaid && <div style={{flex:"0 0 auto",fontSize:"10px",color:"#2E86C1",fontFamily:"'Space Mono',monospace",whiteSpace:"nowrap"}}>{fmt(r2(r.amount/freqMonths))}/mo</div>}
                  <button onClick={()=>setRenewals(renewals.filter((_,i)=>i!==idx))} style={{padding:"4px 8px",borderRadius:"6px",border:"1px solid rgba(192,57,43,0.15)",background:"transparent",color:"#C0392B",cursor:"pointer",fontSize:"14px",lineHeight:1,fontFamily:"'DM Sans',sans-serif"}}>{"\u00D7"}</button>
                </div>
              ))}
            </div>}
            {renewals.filter(r=>r.amount>0).length>0 && <div style={{marginTop:"10px",padding:"10px 12px",borderRadius:"8px",background:"rgba(46,134,193,0.04)",border:"1px solid rgba(46,134,193,0.08)"}}>
              <div style={{fontSize:"10px",fontWeight:600,color:"#2E86C1",marginBottom:"6px"}}>PRICING SCHEDULE</div>
              <div style={{display:"flex",gap:"6px",flexWrap:"wrap",alignItems:"center"}}>
                <div style={{fontSize:"10px",padding:"3px 8px",borderRadius:"5px",background:"#fff",border:"1px solid rgba(0,0,0,0.06)"}}>
                  <span style={{color:"#8B8FA3"}}>Current</span> <span style={{fontFamily:"'Space Mono',monospace",fontWeight:600}}>{fmt(f.amount || (isPrepaid ? (Math.max(...forecastMonths.map(m=>f.monthlyData[m]||0)) * freqMonths) : 0))}</span>
                </div>
                {renewals.filter(r=>r.amount>0).sort((a,b)=>a.month.localeCompare(b.month)).map((r,i)=><React.Fragment key={i}>
                  <span style={{color:"#2E86C1",fontSize:"12px"}}>{"\u2192"}</span>
                  <div style={{fontSize:"10px",padding:"3px 8px",borderRadius:"5px",background:"#fff",border:"1px solid rgba(46,134,193,0.15)"}}>
                    <span style={{color:"#8B8FA3"}}>{formatMonthLabel(r.month, "shortYear")}</span> <span style={{fontFamily:"'Space Mono',monospace",fontWeight:600,color:"#2E86C1"}}>{fmt(r.amount)}</span>
                    {isPrepaid && <span style={{color:"#8B8FA3",fontSize:"9px",marginLeft:"4px"}}>({fmt(r2(r.amount/freqMonths))}/mo)</span>}
                  </div>
                </React.Fragment>)}
              </div>
            </div>}
          </div>}

          {f.type==="per_seat" && <>
            <div>
              <label style={lS}>Cost Per Seat</label>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:"12px",top:"50%",transform:"translateY(-50%)",color:"#8B8FA3",fontSize:"14px",pointerEvents:"none"}}>$</span>
                <input style={{...iS,paddingLeft:"24px",MozAppearance:"textfield"}} type="text" inputMode="decimal"
                  value={f.perSeatCost ? f.perSeatCost.toLocaleString("en-US") : ""}
                  onChange={e=>{const raw=e.target.value.replace(/[^0-9.]/g,"");setF({...f,perSeatCost:parseFloat(raw)||0});}}
                  placeholder="0"/>
              </div>
              <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>Monthly cost per person</div>
            </div>
            <div>
              <label style={lS}>Seat Basis</label>
              <select style={iS} value={f.seatBasis||"fte"} onChange={e=>setF({...f,seatBasis:e.target.value})}>
                {SEAT_BASIS_OPTIONS.map(o=><option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"4px"}}>Which headcount drives the cost</div>
            </div>
            <div style={{gridColumn:"1/-1"}}>
              <label style={lS}>Department Scope</label>
              <label style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",padding:"10px 12px",borderRadius:"8px",background:allDepts?"rgba(39,174,96,0.06)":"rgba(0,0,0,0.02)",border:"1px solid "+(allDepts?"rgba(39,174,96,0.2)":"rgba(0,0,0,0.04)"),marginBottom:"10px",transition:"all 0.15s"}}>
                <input type="checkbox" checked={allDepts} onChange={e=>{
                  const checked = e.target.checked;
                  setAllDepts(checked);
                  if (checked) setF({...f, seatDepartments: []});
                }} style={{width:"16px",height:"16px",accentColor:"#27AE60",cursor:"pointer"}}/>
                <span style={{fontSize:"12px",fontWeight:600,color:allDepts?"#27AE60":"#6B7094"}}>All Departments</span>
                <span style={{fontSize:"10px",color:"#8B8FA3",fontWeight:400,marginLeft:"4px"}}>Company-wide headcount</span>
              </label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"8px",opacity:allDepts?0.4:1,pointerEvents:allDepts?"none":"auto",transition:"opacity 0.15s"}}>
                {SEAT_DEPARTMENTS.map(d=>{
                  const checked = !allDepts && (f.seatDepartments||[]).includes(d.id);
                  return <label key={d.id} style={{display:"flex",alignItems:"center",gap:"8px",cursor:allDepts?"default":"pointer",padding:"8px 12px",borderRadius:"8px",background:checked?"rgba(46,134,193,0.06)":"rgba(0,0,0,0.02)",border:"1px solid "+(checked?"rgba(46,134,193,0.2)":"rgba(0,0,0,0.04)"),transition:"all 0.15s"}}>
                    <input type="checkbox" checked={checked} disabled={allDepts} onChange={e=>{
                      const deps = f.seatDepartments||[];
                      const updated = e.target.checked ? [...deps, d.id] : deps.filter(x=>x!==d.id);
                      if (updated.length===0) setAllDepts(true);
                      setF({...f, seatDepartments: updated});
                    }} style={{width:"15px",height:"15px",accentColor:"#2E86C1",cursor:allDepts?"default":"pointer"}}/>
                    <span style={{fontSize:"12px",color:checked?"#2E86C1":"#6B7094",fontWeight:checked?600:400}}>{d.label}</span>
                  </label>;
                })}
              </div>
              <div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"6px"}}>Uncheck "All Departments" to select specific departments.</div>
            </div>
          </>}

          {f.type==="pct_revenue" && <div>
            <label style={lS}>% of Revenue</label>
            <div style={{position:"relative"}}>
              {/* B5 (Brian, 2026-10-27): a text draft, 0-100 with at most two decimals, stored as a fraction (2.9 -> 0.029) --
                  utils/percentText.js, the "% of new ACV" box's rule. It used to keep DIGITS ONLY, so a typed 2.9 stored 29%. */}
              <input style={{...iS,paddingRight:"30px",MozAppearance:"textfield"}} type="text" inputMode="decimal"
                value={pctText}
                onChange={e=>{const t=e.target.value;setPctText(t);const pr=parsePercentText(t,PCT_RATE_MESSAGES);if(!pr.error)setF({...f,pctRate:pr.rate});}}
                placeholder="9"/>
              <span style={{position:"absolute",right:"12px",top:"50%",transform:"translateY(-50%)",color:"#8B8FA3",fontSize:"14px",fontWeight:600,pointerEvents:"none"}}>%</span>
            </div>
            {pctTextError && <div data-pct-rate-error="1" style={{fontSize:"11px",color:"#C0392B",fontWeight:600,marginTop:"4px"}}>{pctTextError}</div>}
            <div style={{fontSize:"11px",color:"#8B8FA3",marginTop:"4px"}}>Enter 0 to 100, up to two decimals {"\u2014"} e.g. 2.95 for 2.95%</div>
            <label style={{...lS,marginTop:"12px"}}>Source Line</label>
            <select
              style={iS}
              value={f.pctRevenueSource || ""}
              onChange={e=>{setPctSrcBlocked(false);setF({...f,pctRevenueSource:e.target.value || null});}}
            >
              {/* W-073 (b): no default -- a blank is "never answered" and is required; "Total Revenue" is a stored answer. */}
              <option value="" disabled>Choose a revenue base...</option>
              <option value={PCT_REVENUE_TOTAL} title={PCT_REVENUE_TOTAL_HELP}>{PCT_REVENUE_TOTAL_LABEL}</option>
              {pctRevenueSourceOptions.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
            {!f.pctRevenueSource && <div data-pct-src-required="1" style={{fontSize:"11px",color:"#C0392B",fontWeight:600,marginTop:"4px"}}>Required {"\u2014"} choose which revenue this applies to</div>}
            <div style={{fontSize:"11px",color:"#8B8FA3",marginTop:"4px"}}>
              {/* 2026-10-27 (B1): this read "Total Revenue uses combined forecast revenue (AE Quota + Company Quota + legacy)." */}
              Revenue line the percentage multiplies against. {PCT_REVENUE_TOTAL_LABEL} is {PCT_REVENUE_TOTAL_HELP}.
            </div>
          </div>}

          {f.type==="one_off" && <div style={{gridColumn:"1/-1",borderTop:"1px solid rgba(0,0,0,0.06)",paddingTop:"16px",marginTop:"4px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
              <div><div style={{fontSize:"12px",fontWeight:700,color:"#6B7094"}}>BUDGET ENTRIES</div><div style={{fontSize:"10px",color:"#8B8FA3",marginTop:"2px"}}>Add an amount for each month you want to budget. All entries housed under this vendor.</div></div>
              <button onClick={addEntry} style={{padding:"5px 14px",borderRadius:"7px",border:"1px solid rgba(46,134,193,0.2)",background:"rgba(46,134,193,0.05)",color:"#2E86C1",cursor:"pointer",fontSize:"12px",fontWeight:600,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>+ Add Month</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:"8px",maxHeight:"220px",overflowY:"auto",paddingRight:"4px"}}>
              {oneOffEntries.map((entry, idx) => (
                <div key={idx} style={{display:"flex",gap:"10px",alignItems:"center"}}>
                  <select style={{...iS,flex:"0 0 140px"}} value={entry.month} onChange={e=>updateEntry(idx,"month",e.target.value)}>
                    {forecastMonths.map(m=><option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
                  </select>
                  <div style={{position:"relative",flex:1}}>
                    <span style={{position:"absolute",left:"12px",top:"50%",transform:"translateY(-50%)",color:"#8B8FA3",fontSize:"14px",pointerEvents:"none"}}>$</span>
                    <input style={{...iS,paddingLeft:"24px"}} type="text" inputMode="decimal"
                      value={entry.amount ? entry.amount.toLocaleString("en-US") : ""}
                      onChange={e=>{const raw=e.target.value.replace(/[^0-9.]/g,"");updateEntry(idx,"amount",parseFloat(raw)||0);}}
                      placeholder="0"/>
                  </div>
                  {oneOffEntries.length > 1 && <button onClick={()=>removeEntry(idx)} style={{padding:"6px 10px",borderRadius:"6px",border:"1px solid rgba(192,57,43,0.15)",background:"transparent",color:"#C0392B",cursor:"pointer",fontSize:"14px",lineHeight:1,fontFamily:"'DM Sans',sans-serif"}}>{"\u00D7"}</button>}
                </div>
              ))}
            </div>
            {oneOffEntries.filter(e=>e.amount>0).length>0 && <div style={{marginTop:"10px",padding:"10px 12px",borderRadius:"8px",background:"rgba(212,172,13,0.05)",border:"1px solid rgba(212,172,13,0.1)"}}>
              <div style={{fontSize:"10px",fontWeight:600,color:"#B7950B",marginBottom:"6px"}}>BUDGET SUMMARY</div>
              <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
                {oneOffEntries.filter(e=>e.amount>0).map((e,i)=><div key={i} style={{fontSize:"10px",padding:"3px 8px",borderRadius:"5px",background:"#fff",border:"1px solid rgba(0,0,0,0.06)"}}>
                  <span style={{color:"#8B8FA3"}}>{formatMonthLabel(e.month, "shortYear")}</span> <span style={{fontFamily:"'Space Mono',monospace",fontWeight:600}}>{fmt(e.amount)}</span>
                </div>)}
              </div>
              <div style={{fontSize:"11px",fontFamily:"'Space Mono',monospace",fontWeight:700,color:"#B7950B",marginTop:"6px"}}>Total: {fmt(oneOffEntries.reduce((s,e)=>s+e.amount,0))}</div>
            </div>}
          </div>}

          <div style={{gridColumn:"1/-1"}}><label style={lS}>Notes</label><input style={iS} value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} placeholder="Optional context"/></div>

          {/* Contractor Headcount */}
          <div style={{gridColumn:"1/-1",borderTop:"1px solid rgba(0,0,0,0.06)",paddingTop:"16px",marginTop:"4px"}}>
            <label style={{...lS,display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",marginBottom:f.isContractor?"14px":"0"}}>
              <input type="checkbox" checked={f.isContractor||false} onChange={e=>{
                const checked = e.target.checked;
                if (checked && (!f.contractorMonthly || Object.keys(f.contractorMonthly).length === 0)) {
                  const filled = {};
                  const cnt = f.contractorCount ?? 1;
                  forecastMonths.forEach(m => { filled[m] = cnt; });
                  setF({...f, isContractor: true, contractorMonthly: filled});
                } else {
                  setF({...f, isContractor: checked});
                }
              }} style={{width:"16px",height:"16px",accentColor:"#2E86C1",cursor:"pointer"}}/>
              <span style={{fontSize:"12px",fontWeight:700,color:"#6B7094",textTransform:"uppercase",letterSpacing:"0.5px"}}>Contractor</span>
              <span style={{fontSize:"10px",color:"#8B8FA3",fontWeight:400,textTransform:"none",letterSpacing:0}}>Track headcount for this vendor</span>
            </label>
            {f.isContractor && <>
              <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"14px"}}>
                <div style={{flex:"0 0 auto"}}>
                  <label style={lS}>Default Headcount</label>
                  <input type="number" min="0" style={{...iS,width:"100px",fontFamily:"'Space Mono',monospace",textAlign:"center"}}
                    value={f.contractorCount ?? 1}
                    onChange={e=>setF({...f,contractorCount:Math.max(0,parseInt(e.target.value)||0)})}/>
                </div>
                <div style={{flex:"0 0 auto",alignSelf:"flex-end"}}>
                  <button onClick={()=>{
                    const filled = {};
                    forecastMonths.forEach(m=>{filled[m]=f.contractorCount ?? 1;});
                    setF({...f,contractorMonthly:filled});
                  }} style={{padding:"8px 16px",borderRadius:"8px",border:"1px solid rgba(46,134,193,0.2)",background:"rgba(46,134,193,0.05)",color:"#2E86C1",cursor:"pointer",fontSize:"12px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Fill All</button>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:"6px"}}>
                {forecastMonths.map(m=>{
                  const val = f.contractorMonthly?.[m];
                  const hasVal = val !== undefined && val !== null && val !== "";
                  return <div key={m} style={{textAlign:"center"}}>
                    <div style={{fontSize:"9px",color:"#8B8FA3",marginBottom:"3px",fontWeight:600}}>{formatMonthLabel(m, "shortYear")}</div>
                    <input type="number" min="0" style={{...iS,padding:"6px 4px",fontSize:"12px",fontFamily:"'Space Mono',monospace",textAlign:"center",color:hasVal?"#1A1C2E":"#C0C3D0",width:"100%",boxSizing:"border-box"}}
                      value={hasVal?val:""}
                      placeholder="—"
                      onChange={e=>{
                        const v2 = e.target.value === "" ? undefined : Math.max(0,parseInt(e.target.value)||0);
                        const cm = {...(f.contractorMonthly||{})};
                        if (v2 === undefined) { delete cm[m]; } else { cm[m] = v2; }
                        setF({...f,contractorMonthly:cm});
                      }}/>
                  </div>;
                })}
              </div>
              {Object.values(f.contractorMonthly||{}).some(v=>v>0) && <div style={{marginTop:"10px",fontSize:"11px",color:"#6B7094"}}>
                Total contractor-months: <span style={{fontFamily:"'Space Mono',monospace",fontWeight:700}}>{Object.values(f.contractorMonthly||{}).reduce((s,v)=>s+(v||0),0)}</span>
              </div>}
            </>}
          </div>
        </div>

        {/* sc-rework-3: forecast preview is CONSTANT-only (prepaid forecast is the
            contract amortization, shown in the grid + Prepaid tab, not re-derived here). */}
        {f.type==="constant" && !isPrepaid && f.amount > 0 && <div style={{marginTop:"20px",padding:"14px",borderRadius:"10px",background:"rgba(46,134,193,0.04)",border:"1px solid rgba(46,134,193,0.1)"}}>
          <div style={{fontSize:"11px",fontWeight:600,color:"#2E86C1",marginBottom:"8px"}}>FORECAST PREVIEW ({forecastYearRangeLabel})</div>
          <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
            {forecastMonths.map(m=>{
              const active = isMonthActive(m);
              let effAmt = f.amount;
              for (const r of renewals.filter(r=>r.amount>0).sort((a,b)=>a.month.localeCompare(b.month))) {
                if (m >= r.month) effAmt = r.amount;
              }
              const displayAmt = effAmt;
              const isChanged = effAmt !== f.amount;

              return <div key={m} style={{fontSize:"10px",padding:"4px 8px",borderRadius:"6px",background:active?(isChanged?"rgba(46,134,193,0.08)":"#fff"):"#F8F9FB",border:"1px solid "+(active?(isChanged?"rgba(46,134,193,0.25)":"rgba(0,0,0,0.06)"):"rgba(0,0,0,0.03)"),opacity:active?1:0.4}}>
                <div style={{color:"#8B8FA3"}}>{formatMonthLabel(m, "shortYear")}</div>
                <div style={{fontFamily:"'Space Mono',monospace",fontWeight:600,color:active?(isChanged?"#2E86C1":"#1A1C2E"):"#C0C3D0"}}>{active?(displayAmt>0?fmt(displayAmt):"\u2014"):"\u2014"}</div>
              </div>;
            })}
          </div>
        </div>}

        {f.type==="per_seat" && f.perSeatCost > 0 && <div style={{marginTop:"20px",padding:"14px",borderRadius:"10px",background:"rgba(142,68,173,0.04)",border:"1px solid rgba(142,68,173,0.12)"}}>
          <div style={{fontSize:"11px",fontWeight:600,color:"#8E44AD",marginBottom:"6px"}}>PER-SEAT FORECAST</div>
          <div style={{fontSize:"12px",color:"#6B7094"}}>
            Per-seat vendor {"\u2014"} forecast computed from headcount. <span style={{fontFamily:"'Space Mono',monospace",fontWeight:600,color:"#8E44AD"}}>{fmt(f.perSeatCost)}/seat/mo</span> {"\u00D7"} {f.seatBasis==="fte"?"FTE":f.seatBasis==="contractors"?"Contractor":"FTE + Contractor"} headcount
            {(f.seatDepartments&&f.seatDepartments.length>0) ? ` (${f.seatDepartments.map(d=>SEAT_DEPARTMENTS.find(s=>s.id===d)?.label||d).join(", ")})` : " (company-wide)"}.
          </div>
        </div>}

        <div style={{gridColumn:"1/-1",borderTop:"1px solid rgba(0,0,0,0.06)",paddingTop:"16px",marginTop:"20px"}}>
          <label style={{...lS,display:"flex",alignItems:"center",gap:"8px",cursor:"pointer",marginBottom:"0"}}>
            <input type="checkbox" checked={f.active !== false} onChange={e=>setF({...f,active:e.target.checked})} style={{width:"16px",height:"16px",accentColor:"#2E86C1",cursor:"pointer"}}/>
            <span style={{fontSize:"12px",fontWeight:700,color:"#6B7094",textTransform:"uppercase",letterSpacing:"0.5px"}}>Active</span>
            <span style={{fontSize:"10px",color:"#8B8FA3",fontWeight:400,textTransform:"none",letterSpacing:0}}>Inactive vendors are hidden from the main list and excluded from forecast (historical actuals preserved)</span>
          </label>
        </div></ViewOnly>

        {/* Session 5d-1: vendor-has-prepaid delete block (409). Cannot hard-delete
            a vendor with linked prepaid contracts — guide to archive/relink. */}
        {deleteError && deleteError.error === "vendor_has_prepaid" && (
          <div style={{marginTop:"24px",padding:"14px 16px",borderRadius:"10px",background:"rgba(192,57,43,0.06)",border:"1px solid rgba(192,57,43,0.25)"}}>
            <div style={{fontSize:"12px",fontWeight:700,color:"#C0392B",marginBottom:"6px"}}>Cannot remove this vendor</div>
            <div style={{fontSize:"12px",color:"#6B7094",marginBottom:"8px"}}>{deleteError.message}</div>
            <div style={{maxHeight:"140px",overflowY:"auto"}}>
              {(deleteError.tiedContracts || []).map(c => (
                <div key={c.id} style={{fontSize:"11px",color:"#4A4E69",fontFamily:"'Space Mono',monospace",padding:"2px 0"}}>
                  {c.account || c.vendor_name} {"·"} {c.start_month} {"·"} {fmt(c.prepaid_amount)}
                </div>
              ))}
            </div>
            <div style={{fontSize:"11px",color:"#8B8FA3",marginTop:"8px",fontStyle:"italic"}}>Tip: uncheck Active to archive instead — archiving keeps the prepaid link intact.</div>
          </div>
        )}
        {/* any OTHER delete that really failed (not "already gone" -- utils/alreadyGone.js) is shown here; it used to
            reach only the console while the dialog closed as if the delete had worked */}
        {deleteError && deleteError.error === "delete_failed" && (
          <div data-delete-error style={{marginTop:"24px",padding:"14px 16px",borderRadius:"10px",background:"rgba(192,57,43,0.06)",border:"1px solid rgba(192,57,43,0.25)"}}>
            <div style={{fontSize:"12px",fontWeight:700,color:"#C0392B",marginBottom:"6px"}}>Could not remove this vendor</div>
            <div style={{fontSize:"12px",color:"#6B7094"}}>{deleteError.message}</div>
          </div>
        )}

        {/* ★ Item 8b: the READER for persistVendor's refuse. Without this the throw
            would be a console-only event and the modal would still close — a failure
            indistinguishable from a success. */}
        {saveError && (
          <div style={{marginTop:"24px",padding:"14px 16px",borderRadius:"10px",background:"rgba(192,57,43,0.06)",border:"1px solid rgba(192,57,43,0.25)"}}>
            <div style={{fontSize:"12px",fontWeight:700,color:"#C0392B",marginBottom:"6px"}}>Not saved</div>
            <div style={{fontSize:"12px",color:"#6B7094"}}>{saveError}</div>
          </div>
        )}

        <div style={{display:"flex",gap:"12px",marginTop:"28px",justifyContent:"flex-end",alignItems:"center"}}>
          {pctSrcBlocked && <div data-pct-src-blocked="1" style={{flex:1,fontSize:"12px",color:"#C0392B",fontWeight:600,marginRight:"12px"}}>Not saved {"\u2014"} choose which revenue this vendor's percentage applies to (Source Line).</div>}
          {isDirty && <div style={{flex:1,fontSize:"11px",color:"#8B8FA3",fontStyle:"italic",marginRight:"12px"}}>Unsaved changes — click outside won't close.</div>}
          {!isNew&&onDelete&&<ViewOnly canEdit={canEdit}><button onClick={()=>onDelete(f.id)} style={{padding:"10px 18px",borderRadius:"10px",border:"1px solid rgba(192,57,43,0.25)",background:"transparent",color:"#C0392B",cursor:"pointer",fontSize:"13px",fontWeight:600,marginRight:"auto",fontFamily:"'DM Sans',sans-serif"}}>Remove</button></ViewOnly>}
          <button onClick={onClose} style={{padding:"10px 22px",borderRadius:"10px",border:"1px solid rgba(0,0,0,0.12)",background:"transparent",color:"#6B7094",cursor:"pointer",fontSize:"13px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Cancel</button>
          <ViewOnly canEdit={canEdit}><button onClick={()=>{
            // W-073 (b): a % of Revenue vendor must name its revenue base (a line, or Total Revenue) -- never saved blank.
            if (f.type === "pct_revenue" && !f.pctRevenueSource) { setPctSrcBlocked(true); return; }
            // B5: a blank / out-of-range / three-decimal percentage is refused (named beside the box) -- never saved
            if (f.type === "pct_revenue" && pctTextError) return;
            let updated = {...f, type: f.type, renewals: renewals.filter(r=>r.amount>0).sort((a,b)=>a.month.localeCompare(b.month))};

            if (updated.type === "one_off") {
              const md = {};
              actualsMonths.forEach(m => { if (f.monthlyData[m]) md[m] = f.monthlyData[m]; });
              oneOffEntries.forEach(e => { if (e.amount > 0) md[e.month] = (md[e.month]||0) + e.amount; });
              updated.monthlyData = md;
            } else {
              // 2b-i: shared per-type md builder (utils/vendorForecast.js) — the
              // SAME code path the bulk Forecast Wizard uses. constant → historical
              // actuals + synthesized forecast (frequency-aware); pct_revenue /
              // per_seat / prepaid → historical-preserve only (engine computes their
              // forecast from config / contracts). Byte-identical to the prior inline
              // branches (constant synthesizes via the Phase-1 helper; the others
              // keep historical only and the parent save() 0-fills forecast months).
              updated.monthlyData = buildVendorForecastMd(updated, actualsMonths, forecastMonths, { startFallback });
              if (updated.type === "per_seat") {
                updated.perSeatCost = r2(f.perSeatCost || 0);
                updated.seatBasis = f.seatBasis || "fte";
                updated.seatDepartments = f.seatDepartments || [];
              }
            }
            onSave(updated);
          }} style={{padding:"10px 28px",borderRadius:"10px",border:"none",background:isDirty?"linear-gradient(135deg,#27AE60,#2ECC71)":"linear-gradient(135deg,#2E86C1,#3498DB)",color:"#fff",cursor:"pointer",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 20px rgba(46,134,193,0.3)"}}>Save</button></ViewOnly>
        </div>
    </GuardedDialog>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   PREPAID MODAL (Session 5d-4b, reworked vendor-contextual)
   Create / edit a discrete or historical prepaid_contracts row UNDER AN EXISTING
   VENDOR (the Carta case: one vendor, multiple overlapping contracts). Forecast
   prepaid cycles are the Vendor Modal's job (freq + amount + renewals). Create
   REQUIRES a vendor — there is no vendorless create; pre-existing orphan rows
   (vendor_id NULL) stay editable/relinkable here. Option-a fields only (NO
   per-cell amort editing — Session C lever). Mirrors VendorModal 5b-safety.
   ═══════════════════════════════════════════════════════════════════════════════ */
/* PrepaidModal MOVED to components/PrepaidModal.jsx (Build 3 c2) so the Step-6
   wizard can embed the SAME editor instead of growing a lookalike. Imported at the
   top of this file; its props and this page's onSave/onGenerate/onDelete wiring are
   unchanged. */

/* ═══════════════════════════════════════════════════════════════════════════════
   PREPAID TAB COMPONENT
   Computes: monthly amortization (→ Exp Build), new prepaid payments,
   and ending BS balance (→ Balance Sheet)
   ═══════════════════════════════════════════════════════════════════════════════ */
const NO_DROPPED = new Map();
function PrepaidTab({ canEdit = false, vendors, setModal, openPrepaid, prepaidContracts, prepaidStartBalance, bsData, prepaidTies, prepaidAnchor2, prepaidSeamResidual, prepaidHistRollAtCutoff, allMonths, forecastMonths, cutoff, lastForecast, forecastYearRangeLabel, isForecast, DC, DC0, isLineTaxonomy, prepaidDropped, renewsByRef, onRenewsAnswer, onAcceptAllRenews, renewFlagged, onRenewFlagKeep, onRenewFlagDelete, renewNotice }) {
  /* Prepaid renewals part 2: the Renews control + chip for one stored contract (the shared pieces; the parent owns the plans). */
  const renewsOf = c => (renewsByRef && c && c.id != null) ? renewsByRef.get("id:" + c.id) : null;
  const renewsBits = c => { const v = renewsOf(c); if (!v) return null; return (<><PrepaidRenewalChip label={v.parentLabel} /><span style={{marginLeft:"8px"}}><ViewOnly canEdit={canEdit}><PrepaidRenewsCell view={v} onAnswer={a => onRenewsAnswer && onRenewsAnswer(c, a)} /></ViewOnly></span></>); };
  const unansweredRenews = renewsByRef ? [...renewsByRef.values()].filter(v => v.answer == null && !v.dash).length : 0;
  /* PREPAID END MONTH (Brian 2026-09-25): the contracts the model drops (utils/prepaidDisplay, computed ONCE in the parent
     from the one rule). They stay LISTED, greyed and marked, and are summed NOWHERE -- not in a vendor row, a total row or
     a card -- so every figure on this tab is the model's. Empty (nothing dropped) on every vendor with no End Month. */
  const dropped = prepaidDropped || NO_DROPPED;
  const [expanded, setExpanded] = useState({});
  const toggle = name => setExpanded(p => ({...p, [name]: !p[name]}));

  // sc-rework-2 (single-source): prepaid forecast cycles are stored rows in
  // prepaid_contracts (the Prepaid Sch). The old vendor-amount→synth memo and its
  // sc-3 synth-vs-stored de-dup were REMOVED — a vendor is FLAGGED type=prepaid;
  // every cycle is a stored row. allContracts is just the stored contracts.
  const allContracts = useMemo(() => [...(prepaidContracts || [])], [prepaidContracts]);

  // Group contracts by vendor
  const byVendor = useMemo(() => {
    const map = {};
    allContracts.forEach(c => {
      if (!map[c.vendor]) map[c.vendor] = [];
      map[c.vendor].push(c);
    });
    // within a vendor: by start month, each chain's renewals directly after its first contract (Brian 10-29; display only)
    return Object.entries(map).map(([v, cs]) => [v, prepaidDisplayOrder(cs).map(i => cs[i])]).sort(([a],[b]) => compareNamesNaturally(a, b));
  }, [allContracts]);

  // Sub-commit 5b-cleanup item (b): partition byVendor into vendor-linked
  // groups (rendered in the main table) and orphan groups — prepaid contracts
  // with no linked vendor. 5d-4b fix: the match decision is now vendor_id-based
  // (aligned with the 5d-1 delete-block, which counts by vendor_id) instead of
  // the old vendor_name fuzzy match — so a contract with a valid vendor_id but a
  // name variant is no longer wrongly orphaned ("counted but not shown").
  // CRITICAL: both groups remain in `allContracts`/`computed`/`byVendor`, so the
  // totals rows (amort / new prepaid / ending BS balance) and the KPI cards are
  // unchanged — this is display-reorganization only, zero computed-value drift.
  const { matchedGroups, orphanGroups } = useMemo(() => {
    const vIds = new Set(vendors.map(v => v.id));
    const matched = [], orphan = [];
    byVendor.forEach(entry => {
      const contracts = entry[1];
      // Matched when any contract in the group is linked to an existing vendor by
      // vendor_id. Orphan when every contract is genuinely unlinked (vendorId NULL)
      // — the legacy prepaid-only / _archive_ rows.
      const isMatched = contracts.some(c => c.vendorId != null && vIds.has(c.vendorId));
      (isMatched ? matched : orphan).push(entry);
    });
    // sc-rework-4: inject EMPTY groups for ACTIVE flagged-prepaid vendors that have
    // NO contracts yet, so the Vendor Modal's "entered on the Prepaid tab" hand-off
    // isn't a dead end (the vendor is visible here with a pre-filled add). DISPLAY-
    // ONLY: an empty contracts array `[]` contributes 0 to `computed` (which sums
    // allContracts, not these), so the amort/newPrepaid/BS totals + §12b booking are
    // untouched. "Covered" = the vendor already has a contract group (by vendor_id
    // OR by name) — skip those.
    const coveredIds = new Set(), coveredNames = new Set();
    byVendor.forEach(([name, contracts]) => {
      coveredNames.add(name);
      contracts.forEach(c => { if (c.vendorId != null) coveredIds.add(c.vendorId); });
    });
    (vendors || []).forEach(v => {
      const flagged = v.active !== false && v.type === "prepaid";
      if (!flagged || coveredIds.has(v.id) || coveredNames.has(v.name)) return;
      matched.push([v.name, []]);   // empty group (no contracts yet)
    });
    matched.sort((a, b) => compareNamesNaturally(a[0], b[0]));   // re-sort so empties slot in by name
    return { matchedGroups: matched, orphanGroups: orphan };
  }, [byVendor, vendors]);

  // Compute totals per month: amortization, new prepaid payments, BS balance
  const computed = useMemo(() => {
    const amort = {}; const newPrepaid = {}; const bsBal = {};
    allMonths.forEach(m => { amort[m] = 0; newPrepaid[m] = 0; });

    // Sum all contract amortization
    allContracts.forEach(c => {
      if (dropped.has(c)) return;   // cancelled by its vendor's End Month: shown, summed nowhere
      Object.entries(c.amort).forEach(([m, v]) => { if (amort[m] !== undefined) amort[m] = r2(amort[m] + v); });
      // New prepaid payment hits in the start month of the contract
      if (newPrepaid[c.start] !== undefined) newPrepaid[c.start] = r2(newPrepaid[c.start] + c.prepaid);
    });

    // BS balance: Prior + New Prepaid - Amortization
    let bal = prepaidStartBalance;
    allMonths.forEach(m => {
      bal = r2(bal + (newPrepaid[m]||0) - (amort[m]||0));
      bsBal[m] = bal;
    });

    // Vendor-level totals
    const vendorTotals = {};
    byVendor.forEach(([name, contracts]) => {
      const vt = {};
      allMonths.forEach(m => { vt[m] = 0; });
      contracts.forEach(c => {
        if (dropped.has(c)) return;
        Object.entries(c.amort).forEach(([m, v]) => { if (vt[m] !== undefined) vt[m] = r2(vt[m] + v); });
      });
      vendorTotals[name] = vt;
    });

    return { amort, newPrepaid, bsBal, vendorTotals };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byVendor, allMonths, prepaidStartBalance, dropped]);

  // Commit 2 (seam-seed display): the AUTHORITATIVE balance line = the engine's
  // BS prepaid series (bsData.prepaid_expenses) — bs_actuals for m<=cutoff +
  // the seam-seeded forecast (re-seated off bs_actuals[cutoff]) for m>cutoff
  // (Commit 1, c1436f2). Read it directly so the tab == Balance Sheet by
  // construction — NO 3rd roll. (computed.bsBal above is the SEPARATE contract
  // "Schedule (rolled)" reconciliation line — the roll from prepaid_start_balance
  // that the per-contract amort + new-payment rows add up to; shown historically
  // only, and it diverges where the schedule is incomplete.)
  const bsLine = useMemo(() => (bsData && bsData.prepaid_expenses) || {}, [bsData]);
  // Tie-verdict comes from the ENGINE (c1436f2, propagated via ModelProvider) —
  // single source of truth, read not re-derived. prepaidAnchor2 = bs_actuals[cutoff]
  // (the forecast seed); prepaidHistRollAtCutoff = the validation roll @cutoff;
  // prepaidSeamResidual / prepaidTies = the verdict ($5 tolerance). Read directly
  // so the disclaimer can't drift from the engine.

  const thS = {padding:"8px 4px",textAlign:"right",fontSize:"9px",fontWeight:600,color:"#8B8FA3",minWidth:"72px",position:"sticky",top:0,background:"#FAFBFC",zIndex:5};
  // Frozen columns: Vendor(220px) + Len(50px) + Prepaid$(80px)
  const fz1 = {position:"sticky",left:0,zIndex:12,minWidth:"220px"};
  const fz2 = {position:"sticky",left:"220px",zIndex:12,minWidth:"50px"};
  const fz3 = {position:"sticky",left:"270px",zIndex:12,minWidth:"80px",borderRight:"2px solid rgba(0,0,0,0.08)"};
  const fzH1 = {...fz1,zIndex:15,background:"#FAFBFC",top:0};
  const fzH2 = {...fz2,zIndex:15,background:"#FAFBFC",top:0};
  const fzH3 = {...fz3,zIndex:15,background:"#FAFBFC",top:0};
  const mono = {fontFamily:"'Space Mono',monospace"};

  return <>
    {/* The banner, the cards and the table form one section no taller than the visible column (maxHeight 100% of the tab body): the
        table box shrinks inside it, so its horizontal bar is on screen at load; a short table keeps its natural
        height. The unlinked-contracts section stays below it, reached by scrolling the page (it carries the bottom
        padding the column cannot give an overflowing block; diffs/vendor-scroll.txt). */}
    <div style={{flex:"0 0 auto",maxHeight:"100%",minHeight:0,display:"flex",flexDirection:"column"}}>
    <div style={{background:"rgba(46,134,193,0.04)",border:"1px solid rgba(46,134,193,0.1)",borderRadius:"14px",padding:"16px 20px",marginBottom:"20px",flexShrink:0}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:"13px",fontWeight:700,color:"#2E86C1",marginBottom:"4px"}}>Prepaid Expenses (GAAP)</div>
          <div style={{fontSize:"12px",color:"#6B7094"}}>Upfront payments booked as prepaid assets on the BS, amortized monthly to the IS. Ending balance flows to Balance Sheet.</div>
          {/* Commit 2 disclaimer (option b): reads the ENGINE tie-verdict
              (c1436f2 → ModelProvider). No in-tab re-derivation. */}
          {prepaidAnchor2 != null && (
            prepaidTies
              ? <div style={{fontSize:"11px",color:"#27AE60",marginTop:"6px",maxWidth:"640px"}}>
                  The forecast starts from the prepaid balance on the balance sheet at the last closed month ({fmt(prepaidAnchor2)}, {formatMonthLabel(cutoff, "shortYear")}). The contract schedule's history matches the general ledger. {"✓"}
                </div>
              : <div title={`Historical schedule rolls to ${fmt(prepaidHistRollAtCutoff)} @ ${formatMonthLabel(cutoff, "shortYear")} vs GL ${fmt(prepaidAnchor2)} (difference ${fmt(prepaidSeamResidual)}).`} style={{fontSize:"11px",color:"#B7950B",marginTop:"6px",maxWidth:"640px"}}>
                  The forecast starts from the prepaid balance on the balance sheet at the last closed month ({fmt(prepaidAnchor2)}, {formatMonthLabel(cutoff, "shortYear")}). The contract schedule's history does not exactly match the general ledger, so closed months show the general ledger's figures (the difference is in the tooltip).
                </div>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"16px"}}>
          {openPrepaid && <ViewOnly canEdit={canEdit}><button onClick={()=>openPrepaid(null)} style={{padding:"9px 16px",borderRadius:"10px",border:"none",background:"linear-gradient(135deg,#2E86C1,#3498DB)",color:"#fff",cursor:"pointer",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap",boxShadow:"0 4px 16px rgba(46,134,193,0.25)"}}>+ Add Prepaid Contract</button></ViewOnly>}
          {/* Prepaid renewals part 2: answers every UNANSWERED contract with its suggestion; never overwrites an answer. */}
          {onAcceptAllRenews && unansweredRenews > 0 && <ViewOnly canEdit={canEdit}><button onClick={onAcceptAllRenews} title="Answer Renews on every contract you have not answered, using its suggestion. Answers you gave are not changed." style={{padding:"9px 14px",borderRadius:"10px",border:"1px solid rgba(46,134,193,0.4)",background:"#fff",color:"#2E86C1",cursor:"pointer",fontSize:"12px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>Accept all suggestions ({unansweredRenews})</button></ViewOnly>}
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:"10px",color:"#8B8FA3",textTransform:"uppercase",fontWeight:600}}>Ending BS Balance ({lastForecast ? formatMonthLabel(lastForecast, "shortYear") : "—"})</div>
            <div style={{fontSize:"22px",fontWeight:700,color:"#2E86C1",...mono}}>{fmt(bsLine[lastForecast]||0)}</div>
          </div>
        </div>
      </div>
    </div>

    {/* Prepaid renewals part 2: a renewal notice (a collision / duplicate stops a chain) and the edited renewals kept when Renews was turned off. */}
    {renewNotice && <div data-renews-notice="1" style={{padding:"10px 14px",marginBottom:"12px",background:"#FDECEA",border:"1px solid #F5C6C2",borderRadius:"10px",fontSize:"12px",color:"#8B2C24",flexShrink:0}}>{renewNotice}</div>}
    <ViewOnly canEdit={canEdit}><PrepaidRenewsFlagged items={renewFlagged} onKeep={onRenewFlagKeep} onDelete={onRenewFlagDelete} /></ViewOnly>
    {/* KPI cards */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"14px",marginBottom:"20px",flexShrink:0}}>
      {[
        ["Vendors With Prepaid Contracts", matchedGroups.length + orphanGroups.length, "#2E86C1"],   // U14: vendor names with a contract (+ empty groups); the header counts rows typed Prepaid
        ["Active Contracts", allContracts.filter(c=>!dropped.has(c)&&Object.keys(c.amort).some(m=>m>cutoff)).length, "#27AE60"],
        [`${forecastYearRangeLabel} Total Amortization`, fmtK(forecastMonths.reduce((s,m)=>s+(computed.amort[m]||0),0)), "#D4AC0D"],
        [`${forecastYearRangeLabel} New Prepaid Payments`, fmtK(forecastMonths.reduce((s,m)=>s+(computed.newPrepaid[m]||0),0)), "#C0392B"],
      ].map(([l,v,c],i)=>(
        <div key={i} style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"14px",padding:"14px 16px"}}>
          <div style={{fontSize:"10px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"5px"}}>{l}</div>
          <div style={{fontSize:"20px",fontWeight:700,color:c,...mono}}>{v}</div>
        </div>
      ))}
    </div>

    {/* Main table */}
    <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",overflow:"hidden",flex:"0 1 auto",minHeight:0,display:"flex",flexDirection:"column"}}>
      <div style={{flex:"0 1 auto",minHeight:0,overflow:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px",minWidth:"1400px"}}>
          <thead>
            <tr style={{borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
              <th style={{padding:"8px 14px",textAlign:"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3",...fzH1}}>Vendor / Contract</th>
              <th style={{padding:"8px 6px",textAlign:"center",fontSize:"10px",fontWeight:600,color:"#8B8FA3",...fzH2}}>Len</th>
              <th style={{padding:"8px 6px",textAlign:"right",fontSize:"10px",fontWeight:600,color:"#8B8FA3",...fzH3}}>Prepaid $</th>
              {allMonths.map(m=><th key={m} style={{...thS,background:isForecast(m)?"#F0F7FC":"#FAFBFC"}}>{formatMonthLabel(m, "shortYear")}</th>)}
            </tr>
          </thead>
          <tbody>
            {matchedGroups.map(([name, contracts]) => {
              const isExp = expanded[name];
              const isEmpty = contracts.length === 0;            // sc-rework-4: flagged vendor, no contracts yet
              const vt = computed.vendorTotals[name] || {};      // empty group has no vendorTotals entry → all-zero
              const totalPrepaid = contracts.reduce((s,c)=>dropped.has(c)?s:s+c.prepaid,0);
              const nCancelled = contracts.filter(c=>dropped.has(c)).length;
              const vendorObj = vendors.find(v=>v.name===name || v.name.replace(/ /g,"")===name.replace(/ /g,""));
              return [
                <tr key={name} style={{borderBottom:"1px solid rgba(0,0,0,0.06)",cursor:"pointer",background:isExp?"#F5F9FC":"transparent"}} onClick={()=>toggle(name)}>
                  <td style={{padding:"8px 14px",fontWeight:700,fontSize:"12px",...fz1,background:isExp?"#F5F9FC":"#fff"}}>
                    <span style={{color:"#2E86C1",marginRight:"6px",fontSize:"10px"}}>{isExp?"\u25BC":"\u25B6"}</span>
                    <span onClick={e=>{e.stopPropagation();if(vendorObj)setModal(vendorObj);}} style={{cursor:vendorObj?"pointer":"default",borderBottom:vendorObj?"1px dashed rgba(46,134,193,0.4)":"none"}} onMouseEnter={e=>{if(vendorObj)e.currentTarget.style.color="#2E86C1";}} onMouseLeave={e=>e.currentTarget.style.color="inherit"}>{name}</span>
                    <span style={{fontSize:"10px",color:isEmpty?"#B7950B":"#8B8FA3",fontWeight:400,marginLeft:"8px",fontStyle:isEmpty?"italic":"normal"}}>{isEmpty?"no contracts yet":`${contracts.length} contract${contracts.length>1?"s":""}${nCancelled?` \u00b7 ${nCancelled} cancelled`:""}`}</span>
                    {/* 5d-4b: per-vendor shortcut — opens the editor pre-linked to this vendor. */}
                    {openPrepaid && vendorObj && <span onClick={canEdit ? (e=>{e.stopPropagation();openPrepaid({__prelink:vendorObj});}) : (e=>e.stopPropagation())} {...viewOnlyInertProps(canEdit)} style={{marginLeft:"10px",fontSize:"10px",color:"#2E86C1",fontWeight:600,cursor:"pointer"}}>+ Add contract</span>}
                  </td>
                  <td style={{padding:"8px 6px",textAlign:"center",fontSize:"10px",color:"#8B8FA3",...fz2,background:isExp?"#F5F9FC":"#fff"}}></td>
                  <td style={{padding:"8px 6px",textAlign:"right",...mono,fontSize:"10px",fontWeight:600,...fz3,background:isExp?"#F5F9FC":"#fff"}}>{fmtK(totalPrepaid)}</td>
                  {allMonths.map(m=>{const a=vt[m]||0;return <td key={m} style={{padding:"8px 3px",textAlign:"right",...mono,fontSize:"10px",fontWeight:600,color:a!==0?DC(m):DC0(m),background:isForecast(m)?"rgba(46,134,193,0.015)":"transparent"}}>{a!==0?fmtK(a):"\u2014"}</td>;})}
                </tr>,
                ...(isExp ? (isEmpty ? [
                  /* sc-rework-4: empty flagged-prepaid vendor — muted prompt + pre-filled add (prelink). */
                  <tr key={`${name}-empty`} style={{borderBottom:"1px solid rgba(0,0,0,0.03)",background:"#FBFBFC"}}>
                    <td style={{padding:"6px 14px 6px 34px",fontSize:"11px",color:"#8B8FA3",fontStyle:"italic",...fz1,background:"#FBFBFC"}}>
                      No contracts yet {"—"} {openPrepaid && vendorObj && <span onClick={canEdit ? (()=>openPrepaid({__prelink:vendorObj})) : undefined} {...viewOnlyInertProps(canEdit)} style={{color:"#2E86C1",cursor:"pointer",borderBottom:"1px dashed rgba(46,134,193,0.4)",fontStyle:"normal"}}>add one</span>}
                    </td>
                    <td style={{padding:"6px",...fz2,background:"#FBFBFC"}}></td>
                    <td style={{padding:"6px",...fz3,background:"#FBFBFC"}}></td>
                    {allMonths.map(m=><td key={m} style={{padding:"6px 3px",background:isForecast(m)?"rgba(46,134,193,0.01)":"transparent"}}></td>)}
                  </tr>
                ] : contracts.map((c, ci) => { const isX = dropped.has(c); return (
                  <tr key={`${name}-${ci}`} style={{borderBottom:"1px solid rgba(0,0,0,0.03)",background:"#FBFBFC"}}>
                    <td style={{padding:"6px 14px 6px 34px",fontSize:"11px",color:isX?"#B0B3C8":"#6B7094",...fz1,background:"#FBFBFC"}}>
                      {c.account}<span style={{color:"#B0B3C8",marginLeft:"6px",fontSize:"10px"}}>from {formatMonthLabel(c.start, "shortYear")}</span>
                      {isX && <span style={{color:"#B7950B",marginLeft:"8px",fontSize:"10px",fontStyle:"italic"}}>cancelled {"\u2014"} End Month {formatMonthLabel(dropped.get(c), "shortYear")}</span>}
                      {/* sc-rework-3: all prepaid rows are now stored contracts (synth removed) — every row is editable. */}
                      {openPrepaid && c.id != null && <span onClick={()=>openPrepaid(c)} style={{marginLeft:"8px",fontSize:"10px",color:"#2E86C1",cursor:"pointer",borderBottom:"1px dashed rgba(46,134,193,0.4)"}}>Edit</span>}
                      {renewsBits(c)}
                    </td>
                    <td style={{padding:"6px",textAlign:"center",fontSize:"10px",color:"#8B8FA3",...fz2,background:"#FBFBFC"}}>{c.length == null ? "indefinite" : `${c.length}mo`}</td>
                    <td style={{padding:"6px",textAlign:"right",...mono,fontSize:"10px",color:isX?"#C8CAD6":"#4A4E69",...(isX?{textDecoration:"line-through"}:{}),...fz3,background:"#FBFBFC"}}>{fmt(c.prepaid)}</td>
                    {allMonths.map(m=>{const a=c.amort[m]||0;return <td key={m} style={{padding:"6px 3px",textAlign:"right",...mono,fontSize:"9px",color:a!==0?(isX?"#C8CAD6":DC(m)):"#E0E2EA",...(isX&&a!==0?{textDecoration:"line-through"}:{}),background:isForecast(m)?"rgba(46,134,193,0.01)":"transparent"}}>{a!==0?fmt(a):""}</td>;})}
                  </tr>
                ); })) : []),
              ];
            })}

            {/* Totals section */}
            <tr style={{borderTop:"2px solid rgba(0,0,0,0.12)",background:"#FAFBFC"}}>
              <td style={{padding:"10px 14px",fontWeight:700,fontSize:"12px",color:"#1A1C2E",...fz1,background:"#FAFBFC"}}>TOTAL MONTHLY AMORTIZATION</td>
              <td style={{padding:"10px 6px",fontSize:"10px",color:"#8B8FA3",...fz2,background:"#FAFBFC"}}></td>
              <td style={{padding:"10px 6px",fontSize:"10px",color:"#8B8FA3",textAlign:"right",...fz3,background:"#FAFBFC",...mono,fontWeight:600}}>{"\u2192"} Exp Build</td>
              {allMonths.map(m=>{const a=computed.amort[m]||0;return <td key={m} style={{padding:"10px 3px",textAlign:"right",...mono,fontSize:"10px",fontWeight:700,color:a!==0?DC(m):DC0(m),background:isForecast(m)?"#EDF4FA":"#FAFBFC"}}>{a!==0?fmtK(a):"\u2014"}</td>;})}
            </tr>
            <tr style={{background:"#F5F6FA"}}>
              <td style={{padding:"8px 14px",fontWeight:600,fontSize:"11px",color:"#6B7094",...fz1,background:"#F5F6FA"}}>New Prepaid Payments</td>
              <td style={{padding:"8px 6px",...fz2,background:"#F5F6FA"}}></td>
              <td style={{padding:"8px 6px",...fz3,background:"#F5F6FA"}}></td>
              {allMonths.map(m=>{const p=computed.newPrepaid[m]||0;return <td key={m} style={{padding:"8px 3px",textAlign:"right",...mono,fontSize:"10px",color:p!==0?DC(m):DC0(m),fontWeight:p!==0?600:400,background:isForecast(m)?"#EEF6F0":"#F5F6FA"}}>{p!==0?fmtK(p):"\u2014"}</td>;})}
            </tr>
            {/* Commit 2: "Schedule (rolled)" \u2014 the contract roll from the opening
                balance; the per-contract amort + new-payment rows above reconcile
                to THIS line. HISTORICAL ONLY (stops at the seam) \u2014 it diverges from
                GL where the schedule is incomplete (the disclaimer explains). */}
            <tr style={{background:"#F5F6FA"}}>
              <td style={{padding:"8px 14px",fontWeight:600,fontSize:"11px",color:"#6B7094",...fz1,background:"#F5F6FA"}} title="Contract schedule roll from the opening balance; reconciles to the amortization + new-payment rows. Shown for closed months only \u2014 diverges from GL where the schedule is incomplete.">Schedule (rolled)</td>
              <td style={{padding:"8px 6px",...fz2,background:"#F5F6FA"}}></td>
              <td style={{padding:"8px 6px",...fz3,background:"#F5F6FA"}}></td>
              {allMonths.map(m=>{const isFc=m>cutoff;const b=computed.bsBal[m]||0;return <td key={m} style={{padding:"8px 3px",textAlign:"right",...mono,fontSize:"10px",fontWeight:600,color:isFc?"#D0D3DD":"#6B7094",background:isForecast(m)?"#F5F6FA":"#F5F6FA"}}>{isFc?"\u00b7":fmtK(b)}</td>;})}
            </tr>
            {/* Commit 2: ENDING BS BALANCE \u2014 the AUTHORITATIVE line = engine
                bsData.prepaid_expenses (bs_actuals historical + seam-seeded
                forecast). Matches the Balance Sheet by construction. */}
            <tr style={{background:"#EDF4FA",borderTop:"1px solid rgba(46,134,193,0.2)"}}>
              <td style={{padding:"12px 14px",fontWeight:700,fontSize:"12px",color:"#2E86C1",...fz1,background:"#EDF4FA"}}>ENDING BS BALANCE<span style={{fontSize:"9px",color:"#8B8FA3",fontWeight:500,marginLeft:"6px"}}>(B/S Amount)</span></td>
              <td style={{padding:"12px 6px",...fz2,background:"#EDF4FA"}}></td>
              <td style={{padding:"12px 6px",textAlign:"right",fontSize:"10px",color:"#2E86C1",...fz3,background:"#EDF4FA",...mono,fontWeight:600}}>{"\u2192"} Bal Sheet</td>
              {allMonths.map(m=>{const b=bsLine[m]||0;return <td key={m} style={{padding:"12px 3px",textAlign:"right",...mono,fontSize:"10px",fontWeight:700,color:DC(m),background:isForecast(m)?"#E0EDF7":"#EDF4FA"}}>{fmtK(b)}</td>;})}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </div>

    {/* Sub-commit 5b-cleanup item (b) → 5d-4b: prepaid contracts with no linked
        vendor (vendor_id NULL — prepaid-only / _archive_ rows). They ALSO
        contribute to the totals/BS-balance above (they stay in `computed`);
        this section surfaces them separately. As of 5d-4b these are EDITABLE
        and RELINKABLE via the prepaid editor (Edit → set a Linked Vendor). */}
    {orphanGroups.length > 0 && (
      <details style={{marginTop:"24px",flexShrink:0,paddingBottom:"24px"}}>
        <summary style={{cursor:"pointer",padding:"12px 16px",fontSize:"13px",fontWeight:600,color:"#4A4E69",background:"rgba(0,0,0,0.02)",borderRadius:"8px"}}>
          Prepaid Contracts Without Linked Vendor ({orphanGroups.reduce((s,[,cs])=>s+cs.length,0)} contract{orphanGroups.reduce((s,[,cs])=>s+cs.length,0)!==1?"s":""} across {orphanGroups.length} name{orphanGroups.length!==1?"s":""})
        </summary>
        <div style={{padding:"12px 16px",fontSize:"11px",color:"#8B8FA3",fontStyle:"italic"}}>
          These prepaid contracts have no linked vendor row (prepaid-only or archived). Use Edit to fix a contract's fields or relink it to a vendor — relinking just sets the contract's Linked Vendor in the editor.
        </div>
        <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",overflow:"hidden",marginTop:"8px"}}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px",minWidth:"1400px"}}>
              <thead>
                <tr style={{borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
                  <th style={{padding:"8px 14px",textAlign:"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3",minWidth:"220px"}}>Contract (no linked vendor)</th>
                  <th style={{padding:"8px 6px",textAlign:"center",fontSize:"10px",fontWeight:600,color:"#8B8FA3",minWidth:"50px"}}>Len</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontSize:"10px",fontWeight:600,color:"#8B8FA3",minWidth:"80px"}}>Prepaid $</th>
                  {allMonths.map(m=><th key={m} style={{padding:"8px 4px",textAlign:"right",fontSize:"9px",fontWeight:600,color:"#8B8FA3",minWidth:"72px"}}>{formatMonthLabel(m, "shortYear")}</th>)}
                </tr>
              </thead>
              <tbody>
                {/* 5d-4b: one editable row per orphan contract (DB-backed; vendor_id NULL). */}
                {orphanGroups.flatMap(([name, contracts]) => contracts.map((c, ci) => (
                  <tr key={`${name}-${ci}`} style={{borderBottom:"1px solid rgba(0,0,0,0.04)"}}>
                    <td style={{padding:"8px 14px",fontWeight:600,fontSize:"11px",color:"#6B7094",minWidth:"220px"}}>
                      {name}<span style={{fontSize:"10px",color:"#8B8FA3",fontWeight:400,marginLeft:"8px"}}>{c.account}{c.start?` · from ${formatMonthLabel(c.start, "shortYear")}`:""}</span>
                      {openPrepaid && c.id != null && <span onClick={()=>openPrepaid(c)} style={{marginLeft:"8px",fontSize:"10px",color:"#2E86C1",cursor:"pointer",borderBottom:"1px dashed rgba(46,134,193,0.4)"}}>Edit</span>}
                      {renewsBits(c)}
                    </td>
                    <td style={{padding:"8px 6px",textAlign:"center",fontSize:"10px",color:"#8B8FA3"}}>{c.length == null ? "indef." : `${c.length}mo`}</td>
                    <td style={{padding:"8px 6px",textAlign:"right",...mono,fontSize:"10px",fontWeight:600}}>{fmtK(c.prepaid)}</td>
                    {allMonths.map(m=>{const a=c.amort[m]||0;return <td key={m} style={{padding:"8px 3px",textAlign:"right",...mono,fontSize:"10px",color:a!==0?DC(m):DC0(m)}}>{a!==0?fmtK(a):"—"}</td>;})}
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      </details>
    )}
  </>;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   IS SUMMARY with Exp Build drill-down
   Click any IS line item to see the vendor-level detail underneath
   ═══════════════════════════════════════════════════════════════════════════════ */
function ISSummary({ data, setModal, allMonths, forecastMonths, forecastYearRangeLabel, isForecast, DC, DC0, isLineTaxonomy }) {
  const [expanded, setExpanded] = useState({});
  const toggle = id => setExpanded(p => ({...p, [id]: !p[id]}));

  const mono = {fontFamily:"'Space Mono',monospace"};
  const fzH = {position:"sticky",left:0,zIndex:15,background:"#FAFBFC",top:0,minWidth:"280px"};
  const fzC = {position:"sticky",left:0,zIndex:12,background:"#fff",minWidth:"280px"};
  const thM = {padding:"8px 4px",textAlign:"right",fontSize:"9px",fontWeight:600,color:"#8B8FA3",minWidth:"72px",position:"sticky",top:0,background:"#FAFBFC",zIndex:5};

  return <>
    <div style={{background:"rgba(212,172,13,0.04)",border:"1px solid rgba(212,172,13,0.1)",borderRadius:"14px",padding:"16px 20px",marginBottom:"20px",flexShrink:0}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:"13px",fontWeight:700,color:"#D4AC0D",marginBottom:"4px"}}>Income Statement — Expense Build</div>
          <div style={{fontSize:"12px",color:"#6B7094"}}>Full {allMonths.length}-month view. Click any IS line item to drill into vendor-level detail.</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:"10px",color:"#8B8FA3",textTransform:"uppercase",fontWeight:600}}>{forecastYearRangeLabel} Total OpEx</div>
          <div style={{fontSize:"22px",fontWeight:700,color:"#1A1C2E",...mono}}>{fmtK(forecastMonths.reduce((s,m)=>s+(data.grand[m]||0),0))}</div>
        </div>
      </div>
    </div>

    <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",overflow:"hidden",flex:"0 1 auto",minHeight:0,display:"flex",flexDirection:"column"}}>
      <div style={{flex:"0 1 auto",minHeight:0,overflow:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px",minWidth:"1400px"}}>
          <thead>
            <tr style={{borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
              <th style={{padding:"8px 14px",textAlign:"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3",...fzH}}>IS Line Item</th>
              {allMonths.map(m=><th key={m} style={{...thM,background:isForecast(m)?"#F0F7FC":"#FAFBFC"}}>{formatMonthLabel(m, "shortYear")}</th>)}
            </tr>
          </thead>
          <tbody>
            {isLineTaxonomy.sections.map(sec=>{
              const cats = isLineTaxonomy.bySection.get(sec.id) || [];
              const hasData = cats.some(c=>allMonths.some(m=>(data.byCat[c.id]?.totals[m]||0)!==0));
              if(!hasData) return null;
              return [
                /* Section header */
                <tr key={`sec_${sec.id}`}>
                  <td style={{padding:"10px 14px",fontSize:"11px",fontWeight:700,color:sec.color,textTransform:"uppercase",letterSpacing:"1px",borderBottom:"1px solid rgba(0,0,0,0.04)",background:"#F5F6FA",...fzC,background:"#F5F6FA"}} colSpan={1}>{sec.label}</td>
                  {allMonths.map(m=><td key={m} style={{padding:"10px 4px",borderBottom:"1px solid rgba(0,0,0,0.04)",background:"#F5F6FA"}}></td>)}
                </tr>,

                /* Category rows — expandable */
                ...cats.filter(c=>allMonths.some(m=>(data.byCat[c.id]?.totals[m]||0)!==0)).map(cat=>{
                  const isExp = expanded[cat.id];
                  const vList = (data.byCat[cat.id]?.vendors||[]).slice().sort((a,b)=>compareNamesNaturally(a.name,b.name));
                  return [
                    <tr key={cat.id} style={{borderBottom:"1px solid rgba(0,0,0,0.04)",cursor:"pointer",background:isExp?"#F8FBFE":"transparent"}} onClick={()=>toggle(cat.id)}>
                      <td style={{padding:"8px 14px 8px 28px",fontSize:"12px",color:"#4A4E69",...fzC,background:isExp?"#F8FBFE":"#fff"}}>
                        <span style={{color:"#2E86C1",marginRight:"6px",fontSize:"10px"}}>{isExp?"\u25BC":"\u25B6"}</span>
                        {cat.label}
                        <span style={{fontSize:"10px",color:"#B0B3C8",marginLeft:"6px"}}>({vList.length})</span>
                      </td>
                      {allMonths.map(m=>{const t=data.byCat[cat.id]?.totals[m]||0;return <td key={m} style={{padding:"8px 4px",textAlign:"right",...mono,fontSize:"10px",fontWeight:600,color:t!==0?DC(m):DC0(m),background:isForecast(m)&&isExp?"rgba(46,134,193,0.015)":isForecast(m)?"rgba(46,134,193,0.01)":"transparent"}}>{t!==0?fmtK(t):"\u2014"}</td>;})}
                    </tr>,
                    /* Expanded vendor rows */
                    ...(isExp ? vList.map(v=>{
                      return <tr key={v.id} style={{borderBottom:"1px solid rgba(0,0,0,0.02)",background:"#FCFDFE",cursor:"pointer",opacity:v.active===false?0.55:1}} onClick={e=>{e.stopPropagation();setModal(v);}}>
                        <td style={{padding:"5px 14px 5px 48px",fontSize:"11px",color:"#6B7094",...fzC,background:"#FCFDFE"}}>
                          {v.name}
                          <span style={{fontSize:"9px",color:"#B0B3C8",marginLeft:"6px"}}>
                            {v.type==="pct_revenue"?fmtPct(v.pctRate)+" rev":v.type==="prepaid"?"prepaid":v.type==="one_off"?"manual":v.type==="per_seat"?"per seat":""}
                          </span>
                        </td>
                        {allMonths.map(m=>{const a=(v.active===false&&v.type!=="prepaid"&&isForecast(m))?0:(v.monthlyData[m]||0);return <td key={m} style={{padding:"5px 3px",textAlign:"right",...mono,fontSize:"9px",color:a!==0?DC(m):"#E8E9EE",background:isForecast(m)?"rgba(46,134,193,0.008)":"transparent"}}>{a!==0?fmtK(a):""}</td>;})}
                      </tr>;
                    }) : []),
                  ];
                }).flat(),

                /* Section total */
                <tr key={`tot_${sec.id}`} style={{borderBottom:"2px solid rgba(0,0,0,0.06)"}}>
                  <td style={{padding:"10px 14px",fontWeight:700,fontSize:"12px",color:sec.color,...fzC}}>Total {sec.label}</td>
                  {allMonths.map(m=>{const t=data.bySection[sec.id]?.[m]||0;return <td key={m} style={{padding:"10px 4px",textAlign:"right",...mono,fontSize:"11px",fontWeight:700,color:t!==0?DC(m):DC0(m),background:isForecast(m)?"rgba(46,134,193,0.02)":"transparent"}}>{t!==0?fmtK(t):"\u2014"}</td>;})}
                </tr>,
              ];
            })}

            {/* Grand total */}
            <tr style={{borderTop:"2px solid rgba(0,0,0,0.12)",background:"#FAFBFC"}}>
              <td style={{padding:"14px",...fzC,background:"#FAFBFC",fontWeight:700,fontSize:"13px"}}>TOTAL OPERATING EXPENSES</td>
              {allMonths.map(m=>{const t=data.grand[m]||0;return <td key={m} style={{padding:"14px 4px",textAlign:"right",...mono,fontWeight:700,fontSize:"12px",color:DC(m),background:isForecast(m)?"#EDF4FA":"#FAFBFC"}}>{fmtK(t)}</td>;})}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </>;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   BULK-EDIT CONFIRMATION MODAL (Session 5c)
   Per-vendor preview with deselect-before-confirm (Q4). Owns its own value +
   keepIds lifecycle; parent owns busy/error + the onApply orchestration (avoids
   setState-after-unmount). sc 5c-3 wires only set_forecast_type; sc 5c-4 adds the
   other 3 actions.
   ═══════════════════════════════════════════════════════════════════════════════ */
function BulkEditModal({ canEdit = false, action, selected, totalSelected, busy, error, onApply, onCancel, forecastMonths, pctRevenueSourceOptions }) {
  const [value, setValue] = useState('');
  const [keepIds, setKeepIds] = useState(() => new Set(selected.map(v => v.id)));
  // sc 5c-4: humanize helpers per action.
  const typeLabel = id => VENDOR_TYPES.find(t => t.id === id)?.label ?? id;
  const freqLabel = id => PAY_FREQ.find(p => p.id === id)?.label ?? id;
  // W-073 (b): the stored sentinel reads "Total Revenue"; a blank is "(not chosen)", never shown as Total Revenue.
  const sourceLabel = key => key === PCT_REVENUE_TOTAL ? PCT_REVENUE_TOTAL_LABEL
    : (key == null || key === '') ? '(not chosen)'
    : (pctRevenueSourceOptions.find(o => o.key === key)?.label ?? key);
  const endLabel = e => (e === 'until_canceled' || !e) ? 'Until Canceled' : formatMonthLabel(e, "shortYear");
  // sc 5c-4: action metadata map drives title + preview columns/cells; forms + warnings stay inline.
  // sc 5c-4.1: blockIf predicate per action — Apply is blocked when ANY kept
  // vendor matches. Universal rule: ALL bulk actions block prepaid vendors
  // (their settings — freq + amount + amort schedule + sometimes renewals —
  // are coupled, must be edited together via per-vendor Modal).
  // set_pct_revenue_source additionally blocks non-pct vendors (source is
  // inert for them).
  const ACTIONS = {
    set_forecast_type:      { noun: 'Type',       field: 'type',             humanize: typeLabel,   blockIf: v => v.type === 'prepaid' },
    set_billing_frequency:  { noun: 'Frequency',  field: 'freq',             humanize: freqLabel,   blockIf: v => v.type === 'prepaid' },
    set_end_date:           { noun: 'End Date',   field: 'endDate',          humanize: endLabel,    blockIf: v => v.type === 'prepaid' },
    set_pct_revenue_source: { noun: '% Source',   field: 'pctRevenueSource', humanize: sourceLabel, blockIf: v => v.type !== 'pct_revenue' },
  };
  const cfg = ACTIONS[action];
  const iS = { width:"100%",padding:"10px 12px",background:"#F0F1F5",border:"1px solid rgba(0,0,0,0.12)",borderRadius:"8px",color:"#1A1C2E",fontSize:"14px",fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box" };
  const lS = { display:"block",fontSize:"11px",fontWeight:600,color:"#6B7094",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"6px" };
  const dirty = keepIds.size !== selected.length || value !== '';
  const blockedCount = selected.filter(v => keepIds.has(v.id) && cfg.blockIf(v)).length;
  const canApply = value !== '' && keepIds.size > 0 && !busy && blockedCount === 0;
  return (
    <div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.4)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{ if(!busy && !dirty) onCancel(); }}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.1)",borderRadius:"20px",padding:"32px",width:"720px",maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,0.15)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"20px"}}>
          <h3 style={{margin:0,fontSize:"20px",fontWeight:700,color:"#1A1C2E"}}>Bulk update — Set {cfg.noun}{dirty && <span style={{color:"#E67E22",fontWeight:700,marginLeft:"6px"}}>*</span>}</h3>
        </div>

        <ViewOnly canEdit={canEdit}><label style={lS}>New {cfg.noun}</label>
        {action === 'set_forecast_type' && (
          <select style={iS} value={value} onChange={e=>setValue(e.target.value)} disabled={busy}>
            <option value="">Choose new type…</option>
            {/* sc 5c-3: prepaid disabled in bulk (operator-discovered during walkthrough) — it needs coupled freq + amount +
                amort schedule (a per-vendor modal workflow, not a single-column bulk). */}
            {VENDOR_TYPES.map(t => (
              <option key={t.id} value={t.id} disabled={t.id === 'prepaid'}>
                {t.id === 'prepaid' ? `${t.label} (use vendor modal)` : t.label}
              </option>
            ))}
          </select>
        )}
        {action === 'set_billing_frequency' && (
          <select style={iS} value={value} onChange={e=>setValue(e.target.value)} disabled={busy}>
            <option value="">Choose new frequency…</option>
            {PAY_FREQ.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        )}
        {action === 'set_end_date' && (
          /* sc 5c-4: mirrors Vendor Modal End Month (L317-321) — until_canceled + YYYY-MM forecast months.
             Bulk offers ALL forecastMonths (no per-vendor start filter; vendors differ). */
          <select style={iS} value={value} onChange={e=>setValue(e.target.value)} disabled={busy}>
            <option value="">Choose new end month…</option>
            <option value="until_canceled">Until Canceled</option>
            {forecastMonths.map(m => <option key={m} value={m}>{formatMonthLabel(m, "shortYear")}</option>)}
          </select>
        )}
        {action === 'set_pct_revenue_source' && (
          /* sc 5c-4: '' = unchosen placeholder. W-073 (b): PCT_REVENUE_TOTAL is STORED as the answer "Total Revenue" (it was
             converted to NULL on Apply, which also meant "never answered"). */
          <select style={iS} value={value} onChange={e=>setValue(e.target.value)} disabled={busy}>
            <option value="">Choose source{"\u2026"}</option>
            <option value={PCT_REVENUE_TOTAL} title={PCT_REVENUE_TOTAL_HELP}>{PCT_REVENUE_TOTAL_LABEL}</option>
            {pctRevenueSourceOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        )}
        {action === 'set_forecast_type' && value === 'pct_revenue' && (
          <div style={{marginTop:"8px",padding:"8px 12px",borderRadius:"8px",background:"rgba(230,126,34,0.08)",border:"1px solid rgba(230,126,34,0.25)",fontSize:"11px",color:"#B7660B"}}>
            Vendors set to “% of Revenue” also need a Source Line and % rate — set those per-vendor in the modal, or run “Set % Source”. This action only changes the type.
          </div>
        )}
        {/* sc 5c-4.1: action-blocking warnings (Apply disabled while blockedCount>0).
            Universal prepaid block for type/freq/end_date; non-pct block for % source. */}
        {(action === 'set_forecast_type' || action === 'set_billing_frequency' || action === 'set_end_date') && blockedCount > 0 && (
          <div style={{marginTop:"8px",padding:"8px 12px",borderRadius:"8px",background:"rgba(230,126,34,0.08)",border:"1px solid rgba(230,126,34,0.25)",fontSize:"11px",color:"#B7660B"}}>
            {blockedCount} of the selected vendors are Prepaid — bulk edits don't apply to prepaid vendors. Deselect those rows, or use the per-vendor Modal.
          </div>
        )}
        {action === 'set_pct_revenue_source' && blockedCount > 0 && (
          <div style={{marginTop:"8px",padding:"8px 12px",borderRadius:"8px",background:"rgba(230,126,34,0.08)",border:"1px solid rgba(230,126,34,0.25)",fontSize:"11px",color:"#B7660B"}}>
            {blockedCount} of the selected vendors are not “% of Revenue” type — the source line doesn't apply. Deselect those rows, or use “Set Type” first.
          </div>
        )}

        <div style={{marginTop:"20px",border:"1px solid rgba(0,0,0,0.08)",borderRadius:"12px",overflow:"hidden",maxHeight:"40vh",overflowY:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"13px"}}>
            <thead><tr style={{borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
              {["Keep?","Vendor",`Current ${cfg.noun}`,`New ${cfg.noun}`].map((h,i)=><th key={i} style={{padding:"10px 12px",textAlign:i===0?"center":"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",background:"#FAFBFC",position:"sticky",top:0,zIndex:1}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {selected.map(v=>{
                const keep = keepIds.has(v.id);
                return <tr key={v.id} style={{borderBottom:"1px solid rgba(0,0,0,0.04)",opacity:keep?1:0.45}}>
                  <td style={{padding:"8px 12px",textAlign:"center"}}>
                    <input type="checkbox" checked={keep} disabled={busy}
                      onChange={e=>setKeepIds(p=>{const n=new Set(p);e.target.checked?n.add(v.id):n.delete(v.id);return n;})}
                      style={{width:"14px",height:"14px",accentColor:"#2E86C1",cursor:busy?"not-allowed":"pointer"}}/>
                  </td>
                  <td style={{padding:"8px 12px",fontWeight:600}}>{v.name}</td>
                  <td style={{padding:"8px 12px",color:"#6B7094"}}>{cfg.humanize(v[cfg.field])}</td>
                  <td style={{padding:"8px 12px",textDecoration:keep?"none":"line-through",color:keep?"#1A1C2E":"#B0B3C8",fontWeight:600}}>{value ? cfg.humanize(value) : "—"}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div></ViewOnly>

        <div style={{display:"flex",gap:"12px",marginTop:"24px",justifyContent:"flex-end",alignItems:"center"}}>
          {/* sc 5c-3: backdrop-dismiss gated on dirty (operator-discovered during walkthrough; mirrors 5b-safety Vendor Modal). */}
          {dirty && <div style={{fontSize:"11px",color:"#8B8FA3",fontStyle:"italic",marginRight:"12px"}}>Unsaved changes — click outside won't close</div>}
          <div style={{flex:1,fontSize:"12px",color:"#6B7094"}}>{keepIds.size} of {totalSelected} vendors will be updated</div>
          {error && <div style={{fontSize:"12px",color:"#C0392B",fontWeight:600,marginRight:"8px"}}>{error}</div>}
          <button onClick={onCancel} disabled={busy} style={{padding:"10px 22px",borderRadius:"10px",border:"1px solid rgba(0,0,0,0.12)",background:"transparent",color:"#6B7094",cursor:busy?"not-allowed":"pointer",fontSize:"13px",fontWeight:600,fontFamily:"'DM Sans',sans-serif",opacity:busy?0.6:1}}>Cancel</button>
          <ViewOnly canEdit={canEdit}><button onClick={()=>{ onApply(value, [...keepIds]); /* W-073 (b): the sentinel is sent as-is -- no longer converted to NULL */ }} disabled={!canApply} style={{padding:"10px 28px",borderRadius:"10px",border:"none",background:"linear-gradient(135deg,#2E86C1,#3498DB)",color:"#fff",cursor:canApply?"pointer":"not-allowed",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 20px rgba(46,134,193,0.3)",opacity:canApply?1:0.55}}>{busy ? "Updating&" : "Apply"}</button></ViewOnly>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function VendorDashboard({ companyId }) {
  // (isData is no longer read here: B6 shows the engine's own pct_revenue result, rawData.pctVendorForecast, 2026-10-27.)
  const { markStale, bsData, ensureFresh, payrollHC, contractorHC, rawData, cutoff, onboardingGap, modelState, modelError, canEditModel } = useModel();
  const allMonths = useMemo(() => rawData?.allMonths || [], [rawData?.allMonths]);
  const actualsMonths = useMemo(() => filterActuals(allMonths, cutoff), [allMonths, cutoff]);
  const forecastMonths = useMemo(() => filterForecast(allMonths, cutoff), [allMonths, cutoff]);
  const firstForecast = forecastMonths.length ? forecastMonths[0] : null;
  const lastForecast = forecastMonths.length ? forecastMonths[forecastMonths.length - 1] : null;
  // Year-range labels for cosmetic display (3.5b rendering layer conversion)
  const forecastYearRangeLabel = useMemo(() => {
    if (!forecastMonths.length) return "";
    const firstY = getYear(forecastMonths[0]);
    const lastY  = getYear(forecastMonths[forecastMonths.length - 1]);
    return firstY === lastY ? String(firstY) : `${firstY}–${lastY}`;
  }, [forecastMonths]);
  const actualsYearRangeLabel = useMemo(() => {
    if (!actualsMonths.length) return "";
    const firstY = getYear(actualsMonths[0]);
    const lastY  = getYear(actualsMonths[actualsMonths.length - 1]);
    return firstY === lastY ? String(firstY) : `${firstY}–${lastY}`;
  }, [actualsMonths]);
  // Cutoff-aware cell-color closures (replace module-scope DC/DC0/isForecast helpers)
  const isForecast = useCallback((m) => m > cutoff, [cutoff]);
  const DC = useCallback((m) => m > cutoff ? "#1A1C2E" : "#2E86C1", [cutoff]);
  const DC0 = useCallback((m) => m > cutoff ? "#D0D2DC" : "rgba(46,134,193,0.3)", [cutoff]);

  // ── IS taxonomy — replaces module-scope IS_CATS / SECTIONS / DISABLED_VENDOR_CATS ──
  // Drives all 8 in-page consumers (modal dropdown, computeVendors bucketing,
  // IS Summary table, Prepaid catLabel, All Vendors filter/sort/render,
  // Dashboard By Section panel) from per-tenant gl_line_mappings. See Path C
  // Session 5b grouping (ii) sub-commit 1 for the rationale.
  const isLineTaxonomy = useMemo(
    () => buildISLineTaxonomy(rawData?.glMappings),
    [rawData?.glMappings]
  );
  // First vendor-assignable entry id — used as the modal "+ Add Vendor" default
  // (was hardcoded 'ga_software' pre-refactor; AE Model doesn't have that key).
  const defaultIsCat = useMemo(
    () => defaultVendorIsLine(isLineTaxonomy),
    [isLineTaxonomy]
  );
  // Display-only greyed per-dg payroll leaves (runtime dynPayroll, absent from glMappings)
  // so multi-dg sections (S&M) show their payroll lines greyed like COGS/G&A/R&D. Empty for
  // legacy tenants (no department_mappings) → no-op. NOT added to the taxonomy object.
  const payrollGreyed = useMemo(
    () => buildPayrollGreyedEntries(rawData?.deptMappings, isLineTaxonomy),
    [rawData?.deptMappings, isLineTaxonomy]
  );
  // ★ Item 7: the tenant's per-seat department contract — ONE resolver shared by the
  // modal's option list, the per-seat display overlay, and (via runFullModel) the
  // engine. Legacy tenants (no department_mappings) get the Semitek maps unchanged.
  const seatDeptResolver = useMemo(
    () => buildSeatDeptResolver(rawData?.deptMappings),
    [rawData?.deptMappings]
  );

  // Sub-commit 2: pct_revenue source-line picker options. Filters
  // revenue_module_mappings to module_type !== 'skip' (skip-type lines have no
  // engine driver — would produce $0 multiplier base). Engine + overlay +
  // save handler all wired by Session 5a Item 2 (151e0fb); this only surfaces
  // the UI <select>. NULL pctRevenueSource → combinedRevenue fallback per
  // computeVendor.js:138-145.
  // The option label is the line's OWN name, gl_line_mappings.display_name, matched by key (utils/leafLabels.js) -- it used to be
  // revenue_module_mappings.display_name, a copy the Chart-of-Accounts rename never updates. A key no row owns shows as the key.
  // 2026-10-27 (Brian B2): the options are the ONE owner's (utils/pctRevenueSource.js pctRevenueBaseOptions) -- every line a revenue
  // module forecasts onto, an implementation line routed onto a Skip line and the lines' own rev_misc_ / rev_impl_ leaves included,
  // each labelled by its own name. The rmm rows keep their old order and labels.
  const pctRevenueSourceOptions = useMemo(() => pctRevenueBaseOptions({
    revMappings: rawData?.revMappings, miscLines: rawData?.miscRevenueLines, implLines: rawData?.implementationLines, glMappings: rawData?.glMappings,
  }), [rawData?.revMappings, rawData?.miscRevenueLines, rawData?.implementationLines, rawData?.glMappings]);

  const [vendors, setVendors] = useState([]);
  const [prepaidContracts, setPrepaidContracts] = useState([]);
  const [prepaidStartBalance, setPrepaidStartBalance] = useState(0);
  const [view, setView] = useState("dashboard");
  const [modal, setModal] = useState(null);
  // Session 5d-1: linkage-protection UI state.
  // deleteError holds the 409 vendor_has_prepaid body (keeps the modal open
  // and shows the tied-contract block). toast shows the rename-cascade result.
  const [deleteError, setDeleteError] = useState(null);
  const [toast, setToast] = useState(null);
  // SC-4b (follow-up B): the half-state ("[Vendor] is a regular-expense vendor")
  // is now BLOCKED up-front by the server (409 prepaid_mixed_state) — surfaced in
  // the PrepaidModal's inline error, not an after-the-fact banner. Shared message:
  const mixedStateMsg = (vendorName) => {
    const nm = vendorName || 'This vendor';
    return `${nm} is a regular-expense vendor (it has forecast monthly expense). A vendor can't be both prepaid and regular-expense — they reach the Income Statement through different paths and would conflict. To add a prepaid contract, create a separate vendor (e.g. "${nm} Prepaid").`;
  };
  // Session 5d-4b: PrepaidModal state. prepaidModal = null | 'new' | contract.
  // prepaidError holds a 409/400/404 body; cleared on modal change so it
  // doesn't bleed across opens (same pattern as the 5d-1 deleteError).
  const [prepaidModal, setPrepaidModal] = useState(null);
  const [prepaidError, setPrepaidError] = useState(null);
  useEffect(() => { setPrepaidError(null); }, [prepaidModal]);
  /* Prepaid renewals part 2: the edited renewals kept when Renews was turned off (the operator chooses for each), and a
   * renewal notice (a collision or a 409 duplicate stops a chain -- D5; a failed renewal write). */
  const [renewFlagged, setRenewFlagged] = useState([]);   // [{ ref, label }]
  const [renewNotice, setRenewNotice] = useState(null);    // string
  // Session 5d-1: clear the 409 delete-block whenever the modal opens a
  // different vendor or closes (prevents the block bleeding across modals).
  // Stays put while the SAME modal remains open after a 409 (modal ref
  // unchanged), so the block still shows when it should.
  useEffect(() => { setDeleteError(null); setSaveError(null); }, [modal]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);
  const [filterSection, setFilterSection] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  // ── sc 5c-1: bulk-edit row selection ──
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectAnchorId, setSelectAnchorId] = useState(null); // shift-click anchor
  const [bulkModal, setBulkModal] = useState(null);           // {action} or null
  const [wizardOpen, setWizardOpen] = useState(false);        // cat-1 bulk Forecast Wizard
  const [bulkBusy, setBulkBusy] = useState(false);            // pessimistic loading (Q6)
  const [bulkError, setBulkError] = useState(null);           // sc 5c-3: inline modal error surface
  // ★ Item 8b: vendor-modal save error (the reader for persistVendor's refuse).
  // Cleared whenever the modal changes so it can't bleed across opens — the same
  // pattern as the 5d-1 deleteError / 5d-4b prepaidError just below.
  const [saveError, setSaveError] = useState(null);

  // Load all data from DB
  // ensureFresh() awaited first so loading=false implies rawData is ready (KPIDashboardPage pattern)
  const loadData = useCallback(async () => {
    try {
      await ensureFresh();
      const [vendorRes, prepaidRes, settingsRes] = await Promise.all([
        getVendorsWithActuals(companyId),
        getPrepaidContracts(companyId),
        getCompanySettings(companyId),
      ]);
      // Transform DB vendor rows into the in-memory format the original code expects
      const transformed = vendorRes.data.map(v => ({
        id: v.id,
        name: v.name,
        // ★ Item 8: honest-empty (see ModelProvider's note). "" rather than null —
        // this value feeds the ISLinePicker as a controlled-input value. The old
        // `|| "ga_software"` was also the source of FINDING A: persistVendor writes
        // is_line = v.isCat, so loading + saving a keyless vendor PERSISTED the
        // Semitek guess as stored tenant data (a guess written to disk becomes a
        // fact). 8b refuses the unrouted save outright.
        isCat: v.is_line || "",
        type: v.forecast_type || "constant",
        freq: v.billing_frequency || "monthly",
        amount: parseFloat(v.monthly_amount) || 0,
        pctRate: parseFloat(v.pct_rate) || 0,
        monthlyData: v.monthlyData || {},
        notes: v.notes || "",
        active: v.is_active !== false,
        // ★ Semitek sweep A2: was `|| "2024-01"` (Semitek's historical start). A
        // missing start_date means NO START GATE (computeVendor.js:65-68 —
        // `if (sd && m < sd)`), so it must stay empty here; stamping a month made the
        // read->write round-trip LAUNDER a NULL into a real gate.
        startDate: v.start_date || "",
        endDate: v.end_date || "until_canceled",
        renewals: v.renewals ? (typeof v.renewals === 'string' ? JSON.parse(v.renewals) : v.renewals) : [],
        amortStart: v.amort_start || "",
        isContractor: v.is_contractor || false,
        contractorCount: v.contractor_count || 1,
        contractorMonthly: v.contractor_monthly ? (typeof v.contractor_monthly === 'string' ? JSON.parse(v.contractor_monthly) : v.contractor_monthly) : {},
        perSeatCost: parseFloat(v.per_seat_cost) || 0,
        seatBasis: v.seat_basis || "fte",
        seatDepartments: v.seat_departments ? (typeof v.seat_departments === 'string' ? JSON.parse(v.seat_departments) : v.seat_departments) : [],
        pctRevenueSource: v.pct_revenue_source || null,
      }));
      setVendors(transformed);
      setPrepaidContracts(prepaidRes.data);
      setPrepaidStartBalance(parseFloat(settingsRes.data.prepaid_start_balance) || 0);
      // SC-1 (follow-up B) model-health load-warn: any vendor with linked
      // prepaid_contracts (by vendor_id) but forecast_type<>'prepaid' violates the
      // "linked contract ⟹ prepaid" invariant. Should be 0 (the write enforcement
      // + the rework-close migration cover it); console.warn surfaces a regression.
      const linkedVendorIds = new Set((prepaidRes.data || []).map(c => c.vendorId).filter(x => x != null));
      const violators = transformed.filter(v => linkedVendorIds.has(v.id) && v.type !== 'prepaid');
      if (violators.length) console.warn('[prepaid-invariant] vendors with linked prepaid contracts but forecast_type != prepaid:', violators.map(v => `${v.name}(id=${v.id}, type=${v.type})`).join(', '));
    } catch (err) {
      console.error('Failed to load vendor data:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId, ensureFresh]);

  useEffect(() => { loadData(); }, [loadData]);

  // sc 5c-1: clear selection when the filtered list changes out from under the
  // user (filter context change) — prevents stale ids no longer visible.
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAnchorId(null);
  }, [filterSection, filterType, showArchived, search]);

  // sc 5c-1 (F3): clear selection on view change — selection is only meaningful
  // on the All Vendors list; prevents stale-selection traps across tabs.
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAnchorId(null);
  }, [view]);

  // Compute display vendors with live pct_revenue and per_seat values overlaid
  const displayVendors = useMemo(() => {
    // ── sc-rework-1: prepaid display re-source (mirrors engine runFullModel
    //    §12b sc-2 booking EXACTLY) ─────────────────────────────────────────
    // A prepaid vendor's forecast amortization is computed LIVE from
    // prepaid_contracts — NOT from v.amount/monthlyData (which is becoming 0
    // under the single-source model). To guarantee the grid / Dashboard /
    // Expense-Build forecast figures EQUAL the engine's per-vendor IS prepaid
    // contribution, replicate the engine's contract→vendor linkage, forecast
    // gate, and per-contract r2 accumulation verbatim:
    //   linkage : c.vendorId resolves to a vendor, else fall back to name —
    //             exactly engine §12b `c.vendor_id||c.vendorId||vByName(c.vendor)`
    //             (name fallback fires only when the id doesn't resolve; the
    //             name Map is last-write-wins, same as the engine).
    //   gate    : FORECAST ONLY — engine skips `m <= cutoff`.
    //   rounding: r2 running accumulation per contract — engine does
    //             `isLines[k][m] = r2((prev||0) + amt)`.
    // Built once per recompute (one O(contracts) pass). The per-vendor overlay
    // below ALSO replicates the engine's Axis-2 gate so the grid equals the IS
    // in BOTH cases: modern (no forecast monthlyData → show contract amort) and
    // legacy (has forecast monthlyData → engine books that, so leave as-loaded).
    // Today all prepaid monthlyData=0, so every prepaid vendor takes the modern
    // path. (sc-rework-2 removes the amount→monthlyData write so this is
    // permanent.)
    /* ★★ BUILD 2 — THE SAME SHARED IDENTITY RESOLVER THE ENGINE USES.
     * This overlay MIRRORS section 12b so the grid equals the income statement; it has to
     * move WITH the engine or the two diverge, which is the whole reason the mirror is
     * documented above rather than left implicit.
     * ★ VOCABULARY — THE SLUG, and note the field: on THIS page the vendor row is the
     * transformed shape built at :1284, where the IS-leaf slug lives in `isCat`
     * (`isCat: v.is_line || ""`), NOT in `is_line`. Handing the resolver `vv.is_line`
     * here would compare undefined against every contract leaf, match nothing, and
     * silently stop attributing every prepaid vendor. Measured, not assumed. */
    // ── SC-3 (overlay-skip): mirror §12b's ROUTE resolution so the overlay's
    //    per-vendor forecast == the engine IS contribution for EVERY contract,
    //    old or new. Build labelToLine from rawData.isLines with the SAME rule as
    //    runFullModel §12b (leaves only: !l.type && l.id && l.label), then apply
    //    the SAME precedence per contract: slug-first (c.is_line — SC-2; always a
    //    real leaf id, used directly like §12b); else label single-resolve; SKIP
    //    on no-match OR ambiguous (size>1) — matching the engine's $0. Before
    //    SC-3 the overlay summed every linked contract regardless of routability,
    //    so an OLD label-only ambiguous/unresolvable row (0 live today) would show
    //    amort while the engine booked $0 → divergence. (Third hand-mirror of
    //    §12b's logic — engine + overlay-sum + this overlay-resolve — flagged for
    //    the follow-up F shared-resolver extraction; not extracted here.)
    // Session F: shared resolver (same build + slug-first/label/skip as §12b).
    // Silent here — the reason is ignored; routes iff a leaf resolved.
    const labelToLine = buildPrepaidLabelToLine(rawData?.isLines || []);
    /* PREPAID END MONTH (the churn rule, 2026-09-24): the per-vendor forecast comes from the ONE owner the engine uses
     * (engines/prepaidTermination.js) over the same End-Month-filtered set, so a terminated prepaid vendor's row shows what
     * the model books. Same precedence as before -- the stable id first, then name + line against the leaf the contract routes
     * to (the Dashboard shape keeps the slug in isCat) -- and a contract that routes nowhere still adds nothing. The chip
     * count still counts every linked stored contract. Byte-identical while no vendor carries an End Month. */
    const { amortByVendorId: prepaidFcByVendorId, countByVendorId: prepaidCountByVendorId } =
      prepaidForecastByVendor(prepaidContracts || [], vendors, { cutoff, lineOf: vv => vv.isCat, labelToLine });
    return vendors.map(v => {
      /* pct_revenue -- B6 (Brian, 2026-10-27): the ENGINE'S OWN per-vendor result (runFullModel's pctVendorForecast, what the income
       * statement books), never a re-computation. The hand overlay this replaces multiplied the rate by isData (the IS row) where the
       * engine used another series, and it skipped the engine's start / end gate -- so the grid and the statement could disagree.
       * Before the model has run (no pctVendorForecast yet) the saved values stand, as before; a vendor the engine does not book (an
       * inactive one, or one on a line the chart does not carry) shows 0 in forecast months -- the amount the statement carries. */
      if (v.type === "pct_revenue") {
        const engineFc = rawData?.pctVendorForecast;
        if (!engineFc) return v;  // loading state: preserve saved values
        const series = engineFc[v.id] || {};
        const u = { ...v, monthlyData: { ...v.monthlyData } };
        forecastMonths.forEach(m => { u.monthlyData[m] = series[m] || 0; });
        return u;
      }
      // per_seat overlay — compute from headcount
      if (v.type === "per_seat" && v.perSeatCost) {
        const u = { ...v, monthlyData: { ...v.monthlyData } };
        // ★ Semitek sweep A2: "" = no gate, mirroring the engine exactly
        // (computeVendor `if (sd && m < sd)`); `m < ""` is always false. A "2024-01"
        // here gated the GRID off while the engine gated the vendor ON.
        const sd = v.startDate || "";
        const ed = v.endDate || "until_canceled";
        const basis = v.seatBasis || "fte";
        const deps = v.seatDepartments || [];
        const isAll = !deps || deps.length === 0;
        // ★ Item 7: the inline duplicate of the engine's two maps is DELETED. This
        // overlay and computeVendor now read the SAME resolver, so the grid and the
        // IS cannot disagree about a per-seat vendor's headcount.
        forecastMonths.forEach(m => {
          if (m < sd || (ed !== "until_canceled" && m > ed)) { u.monthlyData[m] = 0; return; }
          let hc = 0;
          if (basis === "fte" || basis === "both") {
            if (payrollHC) {
              if (isAll) { hc += payrollHC.fte?.[m] || 0; }
              else { deps.forEach(d => { const k = seatDeptResolver.fteKey(d); if (k) hc += payrollHC[k]?.[m] || 0; }); }
            }
          }
          if (basis === "contractors" || basis === "both") {
            if (contractorHC) {
              if (isAll) { hc += contractorHC.contractors_total?.[m] || 0; }
              else { const bs = new Set(); deps.forEach(d => { const b = seatDeptResolver.contractorBucket(d); if (b) bs.add(b); }); bs.forEach(b => { hc += contractorHC[b]?.[m] || 0; }); }
            }
          }
          u.monthlyData[m] = r2(v.perSeatCost * hc);
        });
        return u;
      }
      // ── sc-rework-1: prepaid forecast display = live contract amortization ──
      // Forecast months (m>cutoff) show Σ linked-contract amort (r2), built
      // above to mirror the engine sc-2 booking. Historical months stay
      // as-loaded (vendor_monthly_actuals is authoritative; the engine doesn't
      // book history). `_prepaidContracts` carries the linked-contract count for
      // the Amount-column chip (the one cell the monthlyData overlay can't fix).
      // sc-rework-5: canonical prepaid = type (freq arm dropped).
      if (v.type === "prepaid") {
        const count = prepaidCountByVendorId[v.id] || 0;
        // Axis-2 gate — mirror engine runFullModel §12b `hasForecastMonthlyData`:
        //   `!!vend.monthlyData && ALL_M.some(m => m>cutoff && r2(monthlyData[m]||0)!==0)`.
        // If this vendor carries nonzero FORECAST monthlyData (legacy / stale /
        // un-migrated), the engine books THAT (sc-2 skips) — so leave monthlyData
        // as-loaded → grid==IS. Only the modern single-source case (no forecast
        // monthlyData) gets the contract-amort overlay. Either way set the chip count.
        const hasFcMonthlyData = forecastMonths.some(m => r2(v.monthlyData[m] || 0) !== 0);
        if (hasFcMonthlyData) return { ...v, _prepaidContracts: count };
        const fc = prepaidFcByVendorId[v.id] || {};
        const u = { ...v, monthlyData: { ...v.monthlyData }, _prepaidContracts: count };
        forecastMonths.forEach(m => { u.monthlyData[m] = r2(fc[m] || 0); });
        return u;
      }
      return v;
    });
    // ★ Item 7: seatDeptResolver is a real dependency now (the per-seat overlay reads
    // it) — without it the grid would keep resolving departments through a stale
    // tenant map after a company switch.
  }, [vendors, payrollHC, contractorHC, forecastMonths, prepaidContracts, cutoff, rawData?.isLines, rawData?.pctVendorForecast, seatDeptResolver]);
  /* PREPAID DISPLAY (Brian 2026-09-25): the contracts the model drops for a vendor's End Month -- the ONE rule
     (activePrepaidContracts, via utils/prepaidDisplay), called ONCE here over the stored vendors and handed to the Prepaid
     tab, which greys them and sums them nowhere. The Vendor Modal gets the cutoff and the label map so it can run the same
     function on its UNSAVED End Month. Empty while no vendor carries an End Month. */
  const prepaidLabelToLine = useMemo(() => buildPrepaidLabelToLine(rawData?.isLines || []), [rawData?.isLines]);
  const prepaidDropped = useMemo(
    () => prepaidDroppedContracts(prepaidContracts || [], vendors, { cutoff, lineOf: vv => vv.isCat, labelToLine: prepaidLabelToLine }),
    [prepaidContracts, vendors, cutoff, prepaidLabelToLine]);

  const data = useMemo(() => computeVendors(displayVendors, allMonths, isLineTaxonomy, cutoff), [displayVendors, allMonths, isLineTaxonomy, cutoff]);

  // 2b-i: persistVendor is the shared per-vendor write CORE (config dbPayload +
  // vmas) reused by the modal save() AND the bulk Forecast Wizard. It omits the
  // modal-coupled tail (markStale + setModal) — callers add those. writeVmas=false
  // (the wizard one_off carve-out) skips the vmas write so the vendor stays
  // todo/portal-pending instead of being 0-filled into a false "done". Throws on
  // error (callers handle).
  const persistVendor = async (v, { writeVmas = true } = {}) => {
      // ★ Item 8b — REFUSE an unrouted save (FINDING A's other half).
      // 8a stopped the LOAD from coercing a missing is_line to "ga_software"; without
      // this, the write-back at `is_line: v.isCat` below would simply persist "" —
      // still saving a vendor wired to nothing, whose spend computeVendor drops to $0.
      // Refuse at the boundary CLOSEST TO THE HUMAN: the operator is looking at the
      // picker right now. A row written empty here surfaces hours later as a P5
      // blocking on a different screen, with no memory of which save caused it.
      // Mirrors the Step-6 import's B3-12 guard: fail loud, never write the ghost.
      if (!v.isCat) {
        const err = new Error(`"${v.name || 'This vendor'}" has no income-statement line. Pick one before saving — a vendor with no IS line books $0 spend, silently.`);
        err.code = ERR_NO_IS_LINE;
        err.vendorName = v.name || '';
        throw err;
      }
      const dbPayload = {
        name: v.name,
        is_line: v.isCat,
        forecast_type: v.type,
        billing_frequency: v.freq,
        // sc-rework-2 (single-source): a prepaid vendor is a FLAG with NO amount —
        // its cycles live in prepaid_contracts. Force monthly_amount=0 for prepaid
        // (the removed synth was its only reader); other types keep their amount.
        monthly_amount: v.type === "prepaid" ? 0 : (v.amount || 0),
        pct_rate: v.pctRate || 0,
        notes: v.notes || "",
        is_active: v.active !== false,
        // ★ Semitek sweep A2: persist NULL, not Semitek's window start.
        start_date: v.startDate || null,
        end_date: v.endDate || "until_canceled",
        amort_start: v.amortStart || "",
        renewals: v.renewals || [],
        is_contractor: v.isContractor || false,
        contractor_count: v.contractorCount ?? 1,
        contractor_monthly: v.contractorMonthly || {},
        per_seat_cost: v.perSeatCost || 0,
        seat_basis: v.seatBasis || "fte",
        seat_departments: v.seatDepartments || [],
        pct_revenue_source: v.pctRevenueSource || null,
      };
      let vendorId = v.id;
      // Check if this is an existing vendor (numeric id from DB) or new
      if (typeof vendorId === 'number') {
        const res = await apiUpdateVendor(companyId, vendorId, dbPayload);
        // Session 5d-1: server cascades a rename into linked prepaid_contracts
        // and returns the count. Surface it so the operator sees the linkage
        // protection fired (and so PrepaidTab's display names stay in sync).
        const n = res?.data?.cascadedPrepaidCount || 0;
        if (n > 0) {
          setPrepaidContracts(p => p.map(c => c.vendorId === vendorId ? { ...c, vendor: v.name } : c));
          setToast(`Renamed vendor; updated ${n} linked prepaid contract${n === 1 ? '' : 's'}.`);
        }
      } else {
        const res = await apiCreateVendor(companyId, dbPayload);
        vendorId = res.data.id;
      }
      // Persist monthly data as actuals
      // Include ALL forecast months - zero values will clear old data after end date
      if (writeVmas && v.monthlyData) {
        const actuals = [];
        // Save all non-zero months (actuals + forecast)
        Object.entries(v.monthlyData)
          .filter(([_, amt]) => amt !== 0 && amt !== undefined && amt !== null)
          .forEach(([month, amount]) => actuals.push({ month, amount: r2(amount) }));
        // For forecast months with zero/missing data, explicitly write 0 to clear old values
        forecastMonths.forEach(m => {
          if (!v.monthlyData[m] || v.monthlyData[m] === 0) {
            actuals.push({ month: m, amount: 0 });
          }
        });
        if (actuals.length > 0) {
          await bulkUpsertVendorActuals(companyId, vendorId, actuals);
        }
      }
      // Update local state
      const updated = { ...v, id: vendorId };
      setVendors(p => {
        const i = p.findIndex(e => e.id === v.id || e.id === vendorId);
        if (i >= 0) { const u = [...p]; u[i] = updated; return u; }
        return [...p, updated];
      });
      return vendorId;
  };

  // Modal save — persistVendor + the modal-coupled tail (markStale + close).
  // Byte-identical to the pre-2b-i save(): same writes, same tail.
  const save = async (v) => {
    // ★ Item 8b: a failed save must NOT close the modal and report success.
    // Previously this swallowed every error into console.error and then ran
    // markStale() + setModal(null) unconditionally — i.e. a failure looked exactly
    // like a success to the operator (§12.5: "a warning with no reader is not a
    // check"). Now the error is surfaced inline and the modal stays open with the
    // operator's edits intact.
    try {
      await persistVendor(v);
    } catch (err) {
      console.error('Failed to save vendor:', err);
      setSaveError(err?.code === ERR_NO_IS_LINE
        ? err.message
        : (err?.response?.data?.error || err?.message || 'Save failed.'));
      return;
    }
    setSaveError(null);
    markStale();
    setModal(null);
    // RECALCULATE (Brian 2026-09-25): markStale alone left the context on the PRE-save model until some page mounted, so
    // the Prepaid tab and the Balance Sheet read the old balance right after a save. The modal is already closed.
    await ensureFresh();
  };

  // 2b-i: bulk Forecast Wizard save — per-type persist for the operator's CHECKED
  // rows. constant / pct_revenue / per_seat → persistVendor (config + vmas via the
  // shared md builder); one_off → CONFIG-ONLY (writeVmas:false) so it stays
  // todo/portal-pending (no false "done"). Chunked-parallel (~10 at a time, no
  // server change); one markStale() after all chunks. cid-scoped via companyId.
  const saveWizardVendors = async (vendorObjs) => {
    // ★ Item 8b: PRE-VALIDATE, then write — never half-write a chunk. The loop below
    // is Promise.all per chunk, so a throw from persistVendor rejects that chunk with
    // some rows already persisted and the rest silently skipped. Validating the whole
    // set first makes the failure all-or-nothing (the shape the bulk PATCH already
    // uses) and names every offender in one message instead of one per attempt.
    // ★ item 8b: the inline check that stood here is now the SHARED
    // assertVendorRouted (utils/vendorForecast.js) — same predicate, same message,
    // same code, same all-or-nothing timing before any write. It moved because the
    // onboarding Step-6 editor needed the identical refusal and a second copy would
    // have drifted (Principle 0). Defaults reproduce this message byte-for-byte.
    assertVendorRouted(vendorObjs);
    const CHUNK = 10;
    for (let i = 0; i < vendorObjs.length; i += CHUNK) {
      const chunk = vendorObjs.slice(i, i + CHUNK);
      await Promise.all(chunk.map(v => {
        if (v.type === "one_off") return persistVendor(v, { writeVmas: false });
        const withMd = { ...v, monthlyData: buildVendorForecastMd(v, actualsMonths, forecastMonths, { startFallback: firstForecast }) };
        return persistVendor(withMd, { writeVmas: true });
      }));
    }
    markStale();
  };

  const del = async (id) => {
    try {
      if (typeof id === 'number') {
        /* ALREADY GONE (utils/alreadyGone.js): a 404 means this company no longer has the vendor --
           the delete's outcome already holds, so it takes the success path below, with no error. */
        try { await apiDeleteVendor(companyId, id); }
        catch (err) { if (!isAlreadyGone(err)) throw err; }
      }
      setVendors(p => p.filter(e => e.id !== id));
      // sc 5c-3: drop the deleted id from any active bulk selection + clear anchor
      // if it pointed at the deleted row (prevents a dangling shift-click anchor).
      setSelectedIds(p => { const n = new Set(p); n.delete(id); return n; });
      setSelectAnchorId(a => a === id ? null : a);
      setDeleteError(null);
      markStale();
      setModal(null);
      await ensureFresh();   // recalculate after the delete (the modal is closed)
    } catch (err) {
      // Session 5d-1: a vendor with linked prepaid contracts returns 409
      // vendor_has_prepaid. Keep the modal open and show the tied-contract
      // block so the operator can archive or relink instead of losing data.
      if (err?.response?.status === 409 && err.response.data?.error === 'vendor_has_prepaid') {
        setDeleteError(err.response.data);
        return;
      }
      /* any other real failure keeps the dialog open and is shown (VendorModal's "delete_failed" block) */
      console.error('Failed to delete vendor:', err);
      setDeleteError({ error: 'delete_failed', message: deleteFailureText(err) });
    }
  };

  // Session 5d-4b: prepaid create/update. On success re-fetch the authoritative
  // list, markStale (the edit feeds runFullModel's prepaidBalance roll-forward),
  // and close. On 409/400/404 keep the modal open and surface the message.
  /* ── PREPAID RENEWALS PART 2 (Brian 10-29). Every action builds a PLAN with the shared planner (utils/prepaidRenewals; the
   * Step-6 wizard runs the same functions) and performs it through the API: a Yes POSTs the renewals in order, each naming
   * its predecessor's NEW id; an edit PUTs / POSTs / DELETEs as the reconcile says; a No deletes the unedited renewals and
   * shows the edited ones (flagged). A 409 duplicate or a collision stops the chain and is shown. */
  const renewsView = useMemo(() => prepaidRenewsView(prepaidContracts || [], { cutoff }), [prepaidContracts, cutoff]);
  const renewsByRef = useMemo(() => new Map(renewsView.filter(v => v.ref).map(v => [v.ref, v])), [renewsView]);
  const prepaidPlanApi = {
    put: (pid, body) => apiUpdatePrepaid(companyId, pid, body).then(r => r.data),
    post: body => apiCreatePrepaid(companyId, body).then(r => r.data),
    del: pid => apiDeletePrepaid(companyId, pid),
  };
  /* Perform one plan on `list` (the contracts it was planned on); collect its flagged rows and notices. */
  const runPrepaidPlan = async (plan, list, acc) => {
    const byRef = new Map((list || []).map(c => ['id:' + c.id, c]));
    (plan.flagged || []).forEach(f => acc.flagged.push({ ref: f.ref, label: prepaidRowLabel(byRef.get(f.ref)) }));
    (plan.remove || []).forEach(r => acc.done.add(r));
    if (plan.collision) acc.notices.push(prepaidCollisionText(plan.collision, list));
    const res = await executePrepaidPlan(plan, list, prepaidPlanApi);
    if (res.duplicate) acc.notices.push(prepaidDuplicateText(res.duplicate));
    return res;
  };
  const newRenewAcc = () => ({ flagged: [], done: new Set(), notices: [] });
  const finishRenew = async (acc) => {
    setRenewFlagged(prev => [...prev.filter(x => !acc.done.has(x.ref) && !acc.flagged.some(f => f.ref === x.ref)), ...acc.flagged]);
    setRenewNotice(acc.notices.length ? acc.notices.join(' ') : null);
    markStale();
    await loadData();
  };
  const renewFailed = async (err, acc) => {
    console.error('Prepaid renewal write failed:', err);
    acc.notices.push('A renewal change could not be saved' + (err?.response?.data?.message ? ': ' + err.response.data.message : '.') + ' The list shows what was saved.');
    await finishRenew(acc);
  };
  const answerRenews = async (c, answer) => {
    const acc = newRenewAcc();
    try { await runPrepaidPlan(planPrepaidRenewsAnswer(prepaidContracts, 'id:' + c.id, answer, { forecastThrough: lastForecast }), prepaidContracts, acc); await finishRenew(acc); }
    catch (err) { await renewFailed(err, acc); }
  };
  /* "Accept all suggestions": every UNANSWERED contract that can renew takes its suggestion, in list order, each through the
   * same plan as a click; the list is re-read after each one so the next is judged on what was saved. Never overwrites an
   * answer; never runs by itself. */
  const acceptAllRenews = async () => {
    const acc = newRenewAcc();
    try {
      let list = prepaidContracts || [];
      const todo = renewsView.filter(v => v.answer == null && !v.dash && v.ref).map(v => ({ ref: v.ref, suggest: v.suggest }));
      for (const t of todo) {
        if (!list.some(c => 'id:' + c.id === t.ref)) continue;
        await runPrepaidPlan(planPrepaidRenewsAnswer(list, t.ref, t.suggest, { forecastThrough: lastForecast }), list, acc);
        list = (await getPrepaidContracts(companyId)).data || [];
      }
      await finishRenew(acc);
    } catch (err) { await renewFailed(err, acc); }
  };
  const renewFlagKeep = async (ref) => {
    const acc = newRenewAcc(); acc.done.add(ref);
    try { await runPrepaidPlan(planPrepaidFlaggedKeep(ref), prepaidContracts, acc); await finishRenew(acc); } catch (err) { await renewFailed(err, acc); }
  };
  const renewFlagDelete = async (ref) => {
    const acc = newRenewAcc();
    try { await runPrepaidPlan(planPrepaidFlaggedDelete(ref), prepaidContracts, acc); await finishRenew(acc); } catch (err) { await renewFailed(err, acc); }
  };

  const savePrepaid = async (payload, id) => {
    // prepaid renewals part 2: the row before this edit (its successors are judged against it -- D4)
    const before = id != null ? (prepaidContracts || []).find(c => c.id === id) || null : null;
    try {
      let saved;
      if (id != null) saved = (await apiUpdatePrepaid(companyId, id, payload)).data;
      else saved = (await apiCreatePrepaid(companyId, payload)).data;
      setPrepaidError(null);
      /* Prepaid renewals part 2: a changed Renews answer runs that answer's plan; otherwise an edit reconciles the chain the
       * row belongs to (a chain whose root does not renew -- every contract no one answered Yes on -- is untouched). Planned
       * on the list as the server now holds it. */
      const newId = id != null ? id : (saved && saved.id);
      if (newId != null) {
        const acc = newRenewAcc();
        try {
          const list = (await getPrepaidContracts(companyId)).data || [];
          const ref = 'id:' + newId;
          const oldAnswer = before ? (before.renews === true ? true : before.renews === false ? false : null) : null;
          const newAnswer = payload.renews;
          let plan = null;
          if ((newAnswer === true || newAnswer === false) && newAnswer !== oldAnswer) plan = planPrepaidRenewsAnswer(list, ref, newAnswer, { forecastThrough: lastForecast, before });
          else if (before) plan = planPrepaidEdit(list, ref, before, { forecastThrough: lastForecast });
          if (plan && (plan.update.length || plan.create.length || plan.remove.length || plan.relink.length || plan.flagged.length || plan.collision)) {
            // the answer itself was already written by the save above -- do not PUT it again
            await runPrepaidPlan({ ...plan, setAnswer: null }, list, acc);
          }
          setRenewFlagged(prev => [...prev.filter(x => !acc.done.has(x.ref) && !acc.flagged.some(f => f.ref === x.ref)), ...acc.flagged]);
          setRenewNotice(acc.notices.length ? acc.notices.join(' ') : null);
        } catch (err) {
          console.error('Prepaid renewal write failed:', err);
          setRenewNotice('The contract was saved, but its renewals could not be updated' + (err?.response?.data?.message ? ': ' + err.response.data.message : '.') + ' The list shows what was saved.');
        }
      }
      // SC-4c: a prepaid write may have auto-typed the linked vendor to prepaid
      // server-side (SC-1/SC-4b clean case). markStale + loadData() is the COMPLETE
      // refresh — it refetches+TRANSFORMS the vendors list (so the auto-typed vendor
      // immediately reads prepaid: Edit Vendor "Prepaid" + the type-gated overlay
      // shows its contract amort), the contracts + settings, runs ensureFresh (the
      // output IS/BS pages recompute) and the invariant health-warn. Replaces the
      // old contracts-only refetch that left `vendors` stale until a manual reload.
      // loadData never sets loading=true (no flicker) and doesn't touch modal state.
      markStale();
      await loadData();
      setPrepaidModal(null);
    } catch (err) {
      const data = err?.response?.data;
      // SC-4b: the server BLOCKS the half-state up-front (409). Keep the modal open
      // and tell the operator to make a separate vendor — the contract was NOT built.
      if (data && data.error === 'prepaid_mixed_state') { setPrepaidError({ error: 'prepaid_mixed_state', message: mixedStateMsg(data.vendor_name) }); return; }
      if (data && (data.error === 'prepaid_duplicate' || data.error === 'bad_vendor_id')) { setPrepaidError(data); return; }
      if (err?.response?.status === 404) { setPrepaidError({ error: 'not_found', message: 'This prepaid contract no longer exists.' }); return; }
      console.error('Failed to save prepaid contract:', err);
      setPrepaidError({ error: 'unknown', message: 'Save failed.' });
    }
  };
  // SC-4: recurring-series generation. N sequential POSTs (no bulk endpoint),
  // skip-on-409-dup for idempotent re-gen; hardFail on any other error → stop +
  // report. Refresh the LOCAL prepaid list DIRECTLY (sc-4b-fix: not gated behind
  // the heavy recompute) so the Prepaid tab + overlay update at once, THEN
  // markStale + ensureFresh so IS/BS/KPI pick up the new rows. Each row carries
  // vendor_id + is_line(slug) + account(label); the server auto-types the vendor
  // prepaid (SC-1). No vendor-zero / no warn-and-zero (single-source).
  const generateRecurring = async (rows) => {
    let created = 0, skipped = 0, hardFail = false, blockErr = null;
    for (const row of rows) {
      try { await apiCreatePrepaid(companyId, row); created++; }
      catch (err) {
        const data = err?.response?.data;
        if (data?.error === 'prepaid_duplicate') { skipped++; continue; }
        // SC-4b: defensive — the half-state block (the generator's vendor is
        // always already-prepaid via the prelink, so this shouldn't fire, but if
        // it does: stop the series + surface the block, keep the modal open).
        if (data?.error === 'prepaid_mixed_state') { blockErr = { error: 'prepaid_mixed_state', message: mixedStateMsg(data.vendor_name) }; hardFail = true; break; }
        console.error('Recurring generate failed mid-series:', err); hardFail = true; break;
      }
    }
    // SC-4c: complete refresh (vendors+contracts+settings+ensureFresh+health-warn)
    // via loadData so any server-side auto-type reflects immediately AND the output
    // IS/BS pages recompute — same as savePrepaid (replaces the contracts-only
    // refetch + standalone ensureFresh).
    markStale();
    await loadData();
    if (blockErr) { setPrepaidError(blockErr); return; }   // keep modal open; no success toast
    setPrepaidError(null);
    setPrepaidModal(null);
    setToast(`Created ${created} contract${created === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped — already existed)` : ''}${hardFail ? ' — stopped early on an error' : ''}.`);
  };

  const delPrepaid = async (id) => {
    try {
      await apiDeletePrepaid(companyId, id);
      const res = await getPrepaidContracts(companyId);
      setPrepaidContracts(res.data);
      markStale();
      setPrepaidModal(null);
      await ensureFresh();   // recalculate after Remove (the prepaid editor is closed)
    } catch (err) {
      console.error('Failed to delete prepaid contract:', err);
      setPrepaidError({ error: 'unknown', message: 'Delete failed.' });
    }
  };

  // sc 5c-1: row-selection toggle with shift-click range support. Range
  // operates over `filtered` (the displayed order = §sort by is_line index then
  // name), matching what the user visually sees. `filtered` is declared below
  // but only read here at event-handler time (post-render), so the forward
  // reference is safe.
  const toggleRow = (id, shift, checked) => {
    if (shift && selectAnchorId != null) {
      const ids = filtered.map(v => v.id);
      const a = ids.indexOf(selectAnchorId), b = ids.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const range = ids.slice(lo, hi + 1);
        setSelectedIds(p => { const n = new Set(p); range.forEach(r => n.add(r)); return n; });
        return;
      }
    }
    setSelectedIds(p => { const n = new Set(p); checked ? n.add(id) : n.delete(id); return n; });
    setSelectAnchorId(id);
  };

  // sc 5c-3: bulk-edit orchestration (Q6 pessimistic). Parent owns busy/error +
  // the success-path teardown so no setState fires on the unmounted modal. On
  // failure the modal stays open with the inline error; value + keepIds persist
  // for retry. loadData() refetches the authoritative vendor shape; markStale()
  // invalidates the engine cache for output pages.
  const onBulkApply = async (value, keepIdsArray) => {
    setBulkBusy(true);
    setBulkError(null);
    try {
      await bulkUpdateVendors(companyId, { vendorIds: keepIdsArray, action: bulkModal.action, value });
      await loadData();
      markStale();
      setSelectedIds(new Set());
      setSelectAnchorId(null);
      setBulkModal(null);
      // recalculate: loadData() above ran ensureFresh BEFORE markStale(), so it never recomputed. The bulk modal is closed.
      await ensureFresh();
    } catch (err) {
      setBulkError(err.response?.data?.error || err.message || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  /* ★ CRASHROOT STEP 2 — this page now reads `modelState`, the provider's single
     terminal-exit answer. Until this build it read NEITHER modelState NOR
     onboardingGap, so a computed-but-HONESTLY-EMPTY tenant (no forecast horizon, or
     no model window) and a THROWN fetch alike cleared this page's own `loading` with
     allMonths===[] — and it spun FOREVER. Both hangs were reproduced on the pre-fix
     tree: diffs/_crashroot2-verifyA-pre.txt.
     'empty' REUSES the existing OnboardingIncomplete JSX (the gap object still names
     the copy); 'error' is the SHARED ModelLoadError added in step 1 — one
     implementation, imported, never an error <div> per page (Principle 0).
     ⛔ THE EXISTING GUARD BELOW IS KEPT, AND STAYS BELOW THESE RETURNS. Belt AND
        braces: this page's `loading` is its OWN flag, which no provider change
        touches; and above these returns the guard would send a named-empty tenant
        straight back to a spinner. */
  if (modelState === 'error') return <ModelLoadError message={modelError} />;
  if (modelState === 'empty') return <OnboardingIncomplete gap={onboardingGap} />;

  /* THE GATE NOW COVERS THE EMPTY WINDOW, NOT JUST `loading` — same reason as DAModule:
     ensureFresh() can resolve without computing, so loading===false does not imply data.
     `allMonths` is bound at :1156 (rawData?.allMonths || []), inside this component. */
  if (modelState === 'loading' || loading || !allMonths.length) return <div style={{padding:"60px",textAlign:"center",color:"#8B8FA3",fontFamily:"'DM Sans',sans-serif"}}>Loading vendor data...</div>;

  const REF = firstForecast || "";

  // cat-1 Forecast Wizard targets: ALL non-prepaid, active vendors (constant +
  // pct_revenue + per_seat + one_off) — prepaid is forecast via its contracts on
  // the Prepaid tab, so it's excluded (matches the wizard's prepaid-not-here note).
  // Selected non-prepaid if any are selected, else all non-prepaid. Uses the base
  // `vendors` (raw historical monthlyData) — the displayVendors overlay only
  // touches forecast months, irrelevant to the wizard's historical run-rate.
  // Plain const (not useMemo) — this is below the loading early-return, so a hook
  // here would be conditional; the filter is cheap.
  const wizardNonPrepaid = vendors.filter(v => (v.type || "constant") !== "prepaid" && v.active !== false);
  const wizardSelected = wizardNonPrepaid.filter(v => selectedIds.has(v.id));
  const wizardTargets = wizardSelected.length ? wizardSelected : wizardNonPrepaid;
  const pill = a => ({padding:"7px 16px",borderRadius:"8px",fontSize:"12px",fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",border:a?"none":"1px solid rgba(0,0,0,0.1)",background:a?"linear-gradient(135deg,#2E86C1,#3498DB)":"transparent",color:a?"#fff":"#6B7094",transition:"all 0.2s"});

  // Filtered vendors for list view
  const filtered = displayVendors.filter(v => {
    if (!showArchived && !v.active) return false;
    if (filterSection !== "all" && isLineTaxonomy.byKey.get(v.isCat)?.section !== filterSection) return false;
    if (filterType !== "all" && (v.type==="prepaid"?"prepaid":"direct") !== filterType) return false;
    if (search && !v.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    const ai = isLineTaxonomy.indexMap.get(a.isCat) ?? 9999;
    const bi = isLineTaxonomy.indexMap.get(b.isCat) ?? 9999;
    if (ai !== bi) return ai - bi;
    return compareNamesNaturally(a.name, b.name);
  });

  const activeCount = displayVendors.filter(v=>v.active).length;
  const prepaidCount = displayVendors.filter(v=>v.active&&v.type==="prepaid").length;
  const directCount = displayVendors.filter(v=>v.active&&v.type!=="prepaid").length;
  const archivedCount = displayVendors.filter(v=>!v.active).length;   // U7 (2026-10-30): the count line names the archived vendors
  const fcTotal = forecastMonths.reduce((s,m)=>s+(data.grand[m]||0),0);

  /* The page fills the shell's page area (height 100%, never the viewport height -- CLAUDE.md section 5; diffs/vendor-scroll.txt).
     The root is a flex column: the title bar keeps its height and the area under it is this page's scroller. On the
     three table views the content column is itself bounded (height 100%, a flex column: the action bar, then the tab
     body, which takes the rest), so each table box takes what is left under the blocks above it and scrolls inside
     itself -- its horizontal bar is on screen at load. A table
     box is flex "0 1 auto" with minHeight 0: it SHRINKS to the space left and never grows, so a short table keeps its
     natural height (no stretched empty box). The Dashboard view has no table: its column keeps its natural height and
     the area scrolls. No viewport-height box is left on these tables. */
  const boundedView = view !== "dashboard";
  return (
    <div style={{fontFamily:"'DM Sans',sans-serif",background:"#F8F9FB",color:"#1A1C2E",height:"100%",display:"flex",flexDirection:"column"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');*{box-sizing:border-box}::-webkit-scrollbar{height:6px;width:6px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.15);border-radius:3px}input:focus,select:focus{border-color:#2E86C1!important;box-shadow:0 0 0 2px rgba(46,134,193,0.2)}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}input[type=number]{-moz-appearance:textfield}select{appearance:none}select option{background:#fff;color:#1A1C2E}`}</style>

      {/* NAV */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 28px",borderBottom:"1px solid rgba(0,0,0,0.08)",background:"rgba(255,255,255,0.97)",backdropFilter:"blur(12px)",position:"sticky",top:0,zIndex:100,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:"14px"}}>
          <div style={{fontSize:"17px",fontWeight:700,letterSpacing:"-0.5px"}}><span style={{color:"#2E86C1"}}>◆</span> Vendor Dashboard</div>
          <div style={{fontSize:"11px",color:"#8B8FA3",padding:"4px 10px",background:"rgba(0,0,0,0.03)",borderRadius:"6px"}}>{activeCount} active vendors · {prepaidCount} prepaid-type · {directCount} direct{archivedCount > 0 ? ` · ${archivedCount} archived` : ""}</div>
        </div>
        <div style={{display:"flex",gap:"5px"}}>
          {[["dashboard","Dashboard"],["vendors","All Vendors"],["prepaid","Prepaid Schedule"],["is_summary","Expense Build"]].map(([v,l])=><button key={v} onClick={()=>setView(v)} style={pill(view===v)}>{l}</button>)}
        </div>
      </div>

      <div style={{flex:1,minHeight:0,overflowY:"auto"}}>
      <div style={{padding:"24px 28px",maxWidth:"1460px",margin:"0 auto",...(boundedView?{height:"100%",display:"flex",flexDirection:"column"}:{})}}>
        {/* Action bar */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"22px",flexShrink:0}}>
          <div style={{fontSize:"13px",color:"#8B8FA3"}}>Actuals {actualsYearRangeLabel} · forecast {forecastYearRangeLabel}. Vendor costs flow to the Prepaid Schedule, the Expense Build and the Income Statement.</div>
          <ViewOnly canEdit={canEditModel}><button onClick={()=>setModal("new")} style={{padding:"9px 22px",borderRadius:"10px",border:"none",background:"linear-gradient(135deg,#2E86C1,#3498DB)",color:"#fff",cursor:"pointer",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 20px rgba(46,134,193,0.25)"}}>+ Add Vendor</button></ViewOnly>
        </div>

        {/* ═══ DASHBOARD ═══ */}
        <div style={boundedView?{flex:1,minHeight:0,display:"flex",flexDirection:"column"}:undefined}>
        {view==="dashboard"&&<>
          {/* KPIs */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"14px",marginBottom:"24px"}}>
            {[
              [`Forecast Monthly (${REF ? formatMonthLabel(REF, "shortYear") : "—"})`,fmtK(data.grand[REF]||0),"#2E86C1"],
              [`${forecastYearRangeLabel} Forecast Total`,fmtK(fcTotal),"#27AE60"],
              ["Prepaid-Type Vendors",prepaidCount,"#D4AC0D"],   // U14: vendor rows typed Prepaid (the Prepaid Schedule counts vendors WITH contracts)
              ["Direct Monthly",directCount,"#C0392B"],
            ].map(([l,v,c],i)=>(
              <div key={i} style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"14px",padding:"16px 18px"}}>
                <div style={{fontSize:"10px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"7px"}}>{l}</div>
                <div style={{fontSize:"24px",fontWeight:700,color:c,fontFamily:"'Space Mono',monospace",letterSpacing:"-1px"}}>{v}</div>
              </div>
            ))}
          </div>

          {/* By IS Section */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:"14px",marginBottom:"24px"}}>
            {isLineTaxonomy.sections.map(sec => {
              const cats = isLineTaxonomy.bySection.get(sec.id) || [];
              const secTotal = data.bySection[sec.id]?.[REF]||0;
              const secFcTotal = forecastMonths.reduce((s,m)=>s+(data.bySection[sec.id]?.[m]||0),0);
              return (
                <div key={sec.id} style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"14px",padding:"18px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"14px"}}>
                    <div style={{width:"4px",height:"18px",borderRadius:"2px",background:sec.color}}/>
                    <div style={{fontSize:"14px",fontWeight:700}}>{sec.label}</div>
                    <div style={{fontSize:"12px",color:"#8B8FA3",marginLeft:"auto",fontFamily:"'Space Mono',monospace"}}>{fmtK(secTotal)}/mo</div>
                  </div>
                  {cats.map(cat=>{
                    const catTotal = data.byCat[cat.id]?.totals[REF]||0;
                    const vCount = data.byCat[cat.id]?.vendors.filter(v=>v.active).length||0;
                    return catTotal>0||vCount>0 ? <div key={cat.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid rgba(0,0,0,0.04)"}}>
                      <span style={{fontSize:"12px",color:"#4A4E69"}}>{cat.label} <span style={{color:"#B0B3C8",fontSize:"10px"}}>({vCount})</span></span>
                      <span style={{fontSize:"12px",fontWeight:600,fontFamily:"'Space Mono',monospace"}}>{fmtK(catTotal)}</span>
                    </div> : null;
                  })}
                  <div style={{display:"flex",justifyContent:"space-between",paddingTop:"10px"}}>
                    <span style={{fontSize:"11px",fontWeight:700,color:"#8B8FA3"}}>{forecastYearRangeLabel} TOTAL</span>
                    <span style={{fontSize:"13px",fontWeight:700,fontFamily:"'Space Mono',monospace",color:sec.color}}>{fmtK(secFcTotal)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Flow diagram */}
          <div style={{padding:"18px 22px",borderRadius:"14px",background:"rgba(46,134,193,0.04)",border:"1px solid rgba(46,134,193,0.1)"}}>
            <div style={{fontSize:"12px",fontWeight:700,color:"#2E86C1",marginBottom:"10px"}}>DATA FLOW</div>
            <div style={{display:"flex",alignItems:"center",gap:"12px",fontSize:"12px",flexWrap:"wrap"}}>
              <div style={{padding:"8px 14px",borderRadius:"8px",background:"#fff",border:"1px solid rgba(0,0,0,0.08)",fontWeight:600}}>Vendor Dashboard</div>
              <span style={{color:"#2E86C1",fontWeight:700}}>→</span>
              <div style={{padding:"8px 14px",borderRadius:"8px",background:"rgba(46,134,193,0.08)",border:"1px solid rgba(46,134,193,0.15)",color:"#2E86C1",fontWeight:600}}>Prepaid Tab ({prepaidCount} vendors)</div>
              <span style={{color:"#2E86C1",fontWeight:700}}>→</span>
              <div style={{padding:"8px 14px",borderRadius:"8px",background:"rgba(39,174,96,0.08)",border:"1px solid rgba(39,174,96,0.15)",color:"#27AE60",fontWeight:600}}>Exp Build (all {activeCount} vendors)</div>
              <span style={{color:"#27AE60",fontWeight:700}}>→</span>
              <div style={{padding:"8px 14px",borderRadius:"8px",background:"rgba(212,172,13,0.08)",border:"1px solid rgba(212,172,13,0.15)",color:"#D4AC0D",fontWeight:600}}>Income Statement</div>
            </div>
          </div>
        </>}

        {/* ═══ ALL VENDORS ═══ */}
        {view==="vendors"&&<>
          <div style={{display:"flex",gap:"10px",marginBottom:"16px",flexWrap:"wrap",alignItems:"center",flexShrink:0}}>
            <input style={{padding:"8px 14px",borderRadius:"8px",border:"1px solid rgba(0,0,0,0.1)",background:"#fff",fontSize:"13px",width:"240px",fontFamily:"'DM Sans',sans-serif",outline:"none"}} placeholder="Search vendors..." value={search} onChange={e=>setSearch(e.target.value)}/>
            <select style={{padding:"8px 14px",borderRadius:"8px",border:"1px solid rgba(0,0,0,0.1)",background:"#fff",fontSize:"12px",fontFamily:"'DM Sans',sans-serif",color:"#4A4E69",outline:"none"}} value={filterSection} onChange={e=>setFilterSection(e.target.value)}>
              <option value="all">All Sections</option>
              {isLineTaxonomy.sections.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <select style={{padding:"8px 14px",borderRadius:"8px",border:"1px solid rgba(0,0,0,0.1)",background:"#fff",fontSize:"12px",fontFamily:"'DM Sans',sans-serif",color:"#4A4E69",outline:"none"}} value={filterType} onChange={e=>setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="prepaid">Prepaid Only</option>
              <option value="direct">Direct Monthly Only</option>
            </select>
            <label style={{display:"flex",alignItems:"center",gap:"6px",cursor:"pointer",fontSize:"12px",color:"#4A4E69",fontFamily:"'DM Sans',sans-serif"}}>
              <input type="checkbox" checked={showArchived} onChange={e=>setShowArchived(e.target.checked)} style={{width:"14px",height:"14px",accentColor:"#2E86C1",cursor:"pointer"}}/>
              Show archived
            </label>
            {/* cat-1: bulk Forecast Wizard entry — sets forecasts for selected
                constant vendors (or all constant when none selected) in one grid. */}
            <ViewOnly canEdit={canEditModel}><button onClick={()=>setWizardOpen(true)} style={{marginLeft:"auto",padding:"8px 16px",borderRadius:"8px",border:"none",background:"linear-gradient(135deg,#27AE60,#2ECC71)",color:"#fff",fontSize:"12px",fontWeight:700,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",boxShadow:"0 2px 10px rgba(39,174,96,0.25)"}}>Bulk Forecast Editor ▸</button></ViewOnly>
            <div style={{fontSize:"12px",color:"#8B8FA3"}}>{filtered.length} vendors</div>
          </div>
          {/* sc 5c-1: contextual bulk-edit toolbar — appears when ≥1 row selected.
              Action buttons disabled until sc 5c-3+ wires the bulk modal/endpoint. */}
          {selectedIds.size > 0 && (
            <div style={{display:"flex",gap:"10px",alignItems:"center",marginBottom:"16px",padding:"10px 16px",borderRadius:"10px",background:"linear-gradient(135deg,rgba(46,134,193,0.08),rgba(52,152,219,0.08))",border:"1px solid rgba(46,134,193,0.2)",flexShrink:0}}>
              <span style={{fontSize:"13px",fontWeight:700,color:"#2E86C1"}}>{selectedIds.size} selected</span>
              {/* sc 5c-4: all 4 actions enabled. */}
              {[
                ["set_forecast_type","Set Type",true],
                ["set_billing_frequency","Set Frequency",true],
                ["set_end_date","Set End Date",true],
                ["set_pct_revenue_source","Set % Source",true],
              ].map(([action,label,enabled])=>(
                <button key={action} disabled={!enabled} onClick={()=>{setBulkError(null);setBulkModal({action});}}
                  style={enabled
                    ? {padding:"7px 14px",borderRadius:"8px",border:"1px solid rgba(46,134,193,0.4)",background:"#fff",fontSize:"12px",fontWeight:600,color:"#2E86C1",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}
                    : {padding:"7px 14px",borderRadius:"8px",border:"1px solid rgba(0,0,0,0.1)",background:"#fff",fontSize:"12px",fontWeight:600,color:"#B0B3C8",cursor:"not-allowed",fontFamily:"'DM Sans',sans-serif",opacity:0.6}}>{label}</button>
              ))}
              <button onClick={()=>{setSelectedIds(new Set());setSelectAnchorId(null);}}
                style={{marginLeft:"auto",padding:"7px 14px",borderRadius:"8px",border:"1px solid rgba(0,0,0,0.1)",background:"transparent",fontSize:"12px",fontWeight:600,color:"#6B7094",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Clear</button>
            </div>
          )}
          <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",overflow:"hidden",flex:"0 1 auto",minHeight:0,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"13px"}}><thead><tr style={{borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
              {/* sc 5c-1: select-all checkbox column (indeterminate when partial) */}
              <th style={{padding:"12px",textAlign:"left",background:"#FAFBFC",position:"sticky",top:0,zIndex:10,width:"36px"}}>
                <ViewOnly canEdit={canEditModel}><input type="checkbox"
                  checked={filtered.length>0 && filtered.every(v=>selectedIds.has(v.id))}
                  ref={el=>{ if(el) el.indeterminate = filtered.some(v=>selectedIds.has(v.id)) && !filtered.every(v=>selectedIds.has(v.id)); }}
                  onChange={e=>{ setSelectedIds(e.target.checked ? new Set(filtered.map(v=>v.id)) : new Set()); setSelectAnchorId(null); }}
                  style={{width:"14px",height:"14px",accentColor:"#2E86C1",cursor:"pointer"}}/></ViewOnly>
              </th>
              {["Vendor","IS Category","Type","Frequency","Amount",`${REF ? formatMonthLabel(REF, "shortYear") : "—"}`,`${forecastYearRangeLabel} Total`,""].map((h,i)=><th key={i} style={{padding:"12px",textAlign:"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",background:"#FAFBFC",position:"sticky",top:0,zIndex:10}}>{h}</th>)}
            </tr></thead><tbody>
              {filtered.map(v=>{
                const cat = isLineTaxonomy.byKey.get(v.isCat);
                const sec = cat ? isLineTaxonomy.sectionById.get(cat.section) : null;
                const isPrepaid = v.type === "prepaid";
                const refAmt = (v.active===false&&!isPrepaid)?0:(v.monthlyData[REF]||0);
                const fcTot = (v.active===false&&!isPrepaid)?0:forecastMonths.reduce((s,m)=>s+(v.monthlyData[m]||0),0);
                return <tr key={v.id} style={{borderBottom:"1px solid rgba(0,0,0,0.04)",cursor:"pointer",opacity:v.active===false?0.8:1,background:selectedIds.has(v.id)?"rgba(46,134,193,0.06)":"transparent"}} onClick={()=>setModal(v)} onMouseEnter={e=>{if(!selectedIds.has(v.id))e.currentTarget.style.background="rgba(0,0,0,0.015)";}} onMouseLeave={e=>{e.currentTarget.style.background=selectedIds.has(v.id)?"rgba(46,134,193,0.06)":"transparent";}}>
                  {/* sc 5c-1: per-row select checkbox — stopPropagation so it doesn't open the modal */}
                  <td style={{padding:"10px 12px",width:"36px"}} onClick={e=>e.stopPropagation()}>
                    <ViewOnly canEdit={canEditModel}><input type="checkbox" checked={selectedIds.has(v.id)}
                      onChange={e=>toggleRow(v.id, e.nativeEvent.shiftKey, e.target.checked)}
                      style={{width:"14px",height:"14px",accentColor:"#2E86C1",cursor:"pointer"}}/></ViewOnly>
                  </td>
                  {/* U7 (2026-10-30): an archived vendor (Show archived) is named as such -- a grey "Archived" badge and a muted, italic name -- not only faded. */}
                  <td style={{padding:"10px 12px",fontWeight:600,...(v.active===false?{color:"#8B8FA3",fontStyle:"italic"}:{})}}>{v.name}{v.active===false&&<span data-archived-badge="1" style={{marginLeft:"8px",padding:"1px 7px",borderRadius:"5px",fontSize:"9px",fontWeight:700,fontStyle:"normal",letterSpacing:"0.4px",textTransform:"uppercase",background:"#E5E7EB",color:"#4B5563",border:"1px solid #D1D5DB"}}>Archived</span>}</td>
                  <td style={{padding:"10px 12px"}}><span style={{padding:"2px 8px",borderRadius:"5px",fontSize:"10px",fontWeight:600,background:(sec?.color||"#888")+"15",color:sec?.color}}>{cat?.label}</span></td>
                  <td style={{padding:"10px 12px",fontSize:"11px"}}>{
                    v.type==="pct_revenue"?<span style={{color:"#C0392B",fontWeight:600}}>{fmtPct(v.pctRate)} rev</span>:
                    v.type==="one_off"?<span style={{color:"#B7950B",fontWeight:600}}>Manual</span>:
                    v.type==="per_seat"?<span style={{color:"#8E44AD",fontWeight:600}}>Per Seat</span>:
                    isPrepaid?<span style={{color:"#2E86C1",fontWeight:600}}>Prepaid</span>:
                    <span style={{color:"#27AE60"}}>Fixed</span>
                  }</td>
                  <td style={{padding:"10px 12px",fontSize:"11px",color:"#8B8FA3"}}>{PAY_FREQ.find(p=>p.id===v.freq)?.label}</td>
                  <td style={{padding:"10px 12px",fontFamily:"'Space Mono',monospace",fontSize:"12px"}}>{v.type==="pct_revenue"?fmtPct(v.pctRate):v.type==="per_seat"?(v.perSeatCost?<>{fmt(v.perSeatCost)}<span style={{fontSize:"9px",color:"#8B8FA3",fontFamily:"'DM Sans',sans-serif"}}>/seat</span></>:"—"):v.type==="prepaid"?<span style={{fontSize:"11px",color:"#2E86C1",fontFamily:"'DM Sans',sans-serif"}}>{(v._prepaidContracts||0)} contract{(v._prepaidContracts||0)===1?"":"s"}</span>:v.amount?fmt(v.amount):"—"}</td>
                  <td style={{padding:"10px 12px",fontFamily:"'Space Mono',monospace",fontWeight:600,fontSize:"12px",color:"#1A1C2E"}}>{fmtK(refAmt)}</td>
                  <td style={{padding:"10px 12px",fontFamily:"'Space Mono',monospace",fontSize:"11px",color:"#1A1C2E"}}>{fmtK(fcTot)}</td>
                  <td style={{padding:"10px 12px",color:"#B0B3C8"}}>→</td>
                </tr>;
              })}
            </tbody></table>
          </div>
        </>}

        {/* ═══ PREPAID EXPENSES TAB ═══ */}
        {view==="prepaid"&&<PrepaidTab canEdit={canEditModel} vendors={displayVendors} setModal={setModal} openPrepaid={(c)=>setPrepaidModal(c||'new')} prepaidContracts={prepaidContracts} prepaidStartBalance={prepaidStartBalance} bsData={bsData} prepaidTies={rawData?.prepaidTies} prepaidAnchor2={rawData?.prepaidAnchor2} prepaidSeamResidual={rawData?.prepaidSeamResidual} prepaidHistRollAtCutoff={rawData?.prepaidHistRollAtCutoff} allMonths={allMonths} forecastMonths={forecastMonths} cutoff={cutoff} lastForecast={lastForecast} forecastYearRangeLabel={forecastYearRangeLabel} isForecast={isForecast} DC={DC} DC0={DC0} isLineTaxonomy={isLineTaxonomy} prepaidDropped={prepaidDropped} renewsByRef={renewsByRef} onRenewsAnswer={answerRenews} onAcceptAllRenews={acceptAllRenews} renewFlagged={renewFlagged} onRenewFlagKeep={renewFlagKeep} onRenewFlagDelete={renewFlagDelete} renewNotice={renewNotice}/>}

        {/* ═══ IS SUMMARY with Exp Build drill-down ═══ */}
        {view==="is_summary"&&<ISSummary data={data} setModal={setModal} allMonths={allMonths} forecastMonths={forecastMonths} forecastYearRangeLabel={forecastYearRangeLabel} isForecast={isForecast} DC={DC} DC0={DC0} isLineTaxonomy={isLineTaxonomy}/>}
        </div>
      </div>
      </div>

      {wizardOpen && <ForecastWizard canEdit={canEditModel} vendors={wizardTargets} forecastMonths={forecastMonths} actualsMonths={actualsMonths} cutoff={cutoff} firstForecast={firstForecast} isLineTaxonomy={isLineTaxonomy} pctRevenueSourceOptions={pctRevenueSourceOptions} onSaveVendors={saveWizardVendors} onClose={()=>{ setWizardOpen(false); ensureFresh(); }} seatDeptOptions={seatDeptResolver.options} saveMode="persist"/>}
      {modal && <VendorModal canEdit={canEditModel} vendor={modal==="new"?null:modal} onSave={save} onClose={()=>{setDeleteError(null);setModal(null);}} onDelete={del} actualsMonths={actualsMonths} forecastMonths={forecastMonths} firstForecast={firstForecast} lastForecast={lastForecast} forecastYearRangeLabel={forecastYearRangeLabel} isLineTaxonomy={isLineTaxonomy} defaultIsCat={defaultIsCat} payrollGreyed={payrollGreyed} pctRevenueSourceOptions={pctRevenueSourceOptions} vendors={vendors} prepaidContracts={prepaidContracts} deleteError={deleteError} saveError={saveError} seatDeptOptions={seatDeptResolver.options} prepaidCutoff={cutoff} prepaidLabelToLine={prepaidLabelToLine}/>}

      {prepaidModal && <PrepaidModal canEdit={canEditModel} contract={(prepaidModal==="new"||prepaidModal?.__prelink)?null:prepaidModal} prelinkVendor={prepaidModal?.__prelink||null} vendors={vendors} isLineTaxonomy={isLineTaxonomy} allMonths={allMonths} firstForecast={firstForecast} lastForecast={lastForecast} onSave={savePrepaid} onGenerate={generateRecurring} onDelete={delPrepaid} onClose={()=>setPrepaidModal(null)} error={prepaidError} saveMode="persist" renewsSuggestion={(prepaidModal && prepaidModal!=="new" && !prepaidModal.__prelink && prepaidModal.id!=null) ? (renewsByRef.get("id:"+prepaidModal.id)||null) : null}/>}

      {/* Session 5d-1: rename-cascade confirmation toast */}
      {toast && (
        <div onClick={()=>setToast(null)} style={{position:"fixed",bottom:"24px",left:"50%",transform:"translateX(-50%)",zIndex:1200,background:"#1A1C2E",color:"#fff",padding:"12px 20px",borderRadius:"10px",fontSize:"13px",fontWeight:600,boxShadow:"0 8px 30px rgba(0,0,0,0.25)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
          {toast}<span style={{marginLeft:"12px",color:"#8B8FA3"}}>{"✕"}</span>
        </div>
      )}

      {bulkModal && (
        <BulkEditModal
          canEdit={canEditModel}
          action={bulkModal.action}
          selected={vendors.filter(v => selectedIds.has(v.id))}
          totalSelected={selectedIds.size}
          busy={bulkBusy}
          error={bulkError}
          onApply={onBulkApply}
          onCancel={() => { setBulkModal(null); setBulkError(null); }}
          forecastMonths={forecastMonths}
          pctRevenueSourceOptions={pctRevenueSourceOptions}
        />
      )}
    </div>
  );
}
